import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { AccentPromptChat } from '../ai/AccentPromptChat';
import { AccentPromptHistory } from '../ai/AccentPromptHistory';
import { AccentRegionSnip } from '../ai/AccentRegionSnip';
import { MAX_PROMPT_IMAGES } from '../ai/AccentPromptComposer';
import { LuCamera } from 'react-icons/lu';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { accentUtils, showDialog, showOK, t } from '../../services/HelperService';
import { LATEST_API_VERSION, normalizeApiVersion } from '../../services/ApiVersionService';
import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { createCustomization, isFrameworkError, saveCustomizationAssetText, saveCustomizationDraft, testCustomizationOutputScript } from '../customizations/customizationApi';
import { sendAssistantChatTurn } from '../ai/assistantChatApi';
import { loadAssistantSession, normalizeSessionHost, openOrCreateAssistantSession, createAssistantSession, rehostAssistantSession, saveAssistantSessionSnapshot } from '../ai/assistantSessionApi';
import { AssistantSessionHeader } from '../ai/AssistantSessionHeader';
import { useAssistantSessionDocuments } from '../ai/useAssistantSessionDocuments';
import { promptAndReportAssistantBug } from '../ai/reportAssistantBug';
import { SaveCustomizationDlg } from '../customizations/SaveCustomizationDlg';
import {
  DependentAssetsCtrl, ProposedAssetCard, buildDependentAssetPromptCatalog, loadAssetLibrary, looksLikeLibraryMaterial, normalizeDependentAssets,
  normalizeProposedAsset, refreshDesignedFromHashes, serializeDependentAssets,
} from '../customizations/DependentAssetsCtrl';
import { testWorkflowSetVariable } from './workflowApi';
import { AiArtifactVersionChip } from '../ai/AiArtifactVersionChip';
import {
  extractHandlebarsTokens,
  collectCatalogTokens,
  isHandlebarsToken,
  isContextPathToken,
  useAiChatAssistant,
} from '../ai/useAiChatAssistant';
import { mapFixedPicklists, mapCatalogGroup, toODataStringLiteral, toSqlStringLiteral, toJavascriptStringLiteral } from './templates/fixedPicklistLiteralCatalog';
import { PRODUCTS_TEMPLATE_GROUP, toPromptProductReference } from './templates/productCatalog';
import { hasLookupReferenceGroups, mapLookupGroupsForPrompt } from './templates/lookupCatalog';
import { normalizeJavascriptCatalog } from './AccentWorkflowJavascriptEditor';
import { isServerOwnedTemplateToken } from './ReportDateRangePicker';
import {
  QUERY_SHAPING_DEFAULT_PROFILE,
  resolveQueryShapingProfile,
  setVariableFromQueryBindingPath,
} from './queryShapingSessionProfiles';
import { DefaultQueryShapingPreview } from './DefaultQueryShapingPreview';
import { ChangeReviewChecklistPanel, parseChecklistGroups, serializeChecklistGroups } from './ChangeReviewChecklistPanel';
import { IntakeQuestionsCard, parseIntakeQuestions, formatIntakeAnswerLine, resolveIntakeIntent } from './IntakeQuestionsCard';
import { PlanPanel, parsePlan, serializePlan } from './PlanPanel';

const q_TestQuery = queryNoTracking('InsyteWebApi_TestQuery', 0, true);

const STATUS_IDLE = 'idle';
const STATUS_GENERATING = 'generating';
const STATUS_TESTING = 'testing';
// Server-kept sessions: how long after the last state change the dialog syncs its
// state to the session document, and how often it asks after a turn that was running
// when it opened.
const SNAPSHOT_SYNC_DELAY_MS = 1500;
const SERVER_TURN_POLL_MS = 5000;

const safeString = (v) => (v ?? '').toString();

/**
 * The JavaScript editors' right-click catalog from a host's javascriptTemplateCatalog (or its templateCatalog when the
 * host has no separate one): handlebars tokens rewritten to ctx.* snippets, fixed picklists and product guids as JS
 * string literals.
 */
export const buildJavascriptMenuCatalog = (javascriptTemplateCatalog, templateCatalog = null) => {
  const base = Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0
    ? javascriptTemplateCatalog
    : (Array.isArray(templateCatalog) ? templateCatalog : []);
  return mapCatalogGroup(
    normalizeJavascriptCatalog(mapFixedPicklists(base, toJavascriptStringLiteral), 'ctx'),
    PRODUCTS_TEMPLATE_GROUP,
    toJavascriptStringLiteral
  );
};

/**
 * The prompt composer's right-click catalog from the JavaScript one: the Products group inserts "Name (ProductID guid)"
 * and lookup groups (reps, suppliers, statuses) insert "Name (<lookupKey> <id>)" so the assistant can resolve them.
 * Shared with hosts that offer the same menu outside the dialog (the supplier integration brief wizard).
 */
export const buildPromptMenuCatalog = (jsCatalog) => mapLookupGroupsForPrompt(mapCatalogGroup(jsCatalog, PRODUCTS_TEMPLATE_GROUP, toPromptProductReference));

/**
 * Prompt-only catalog groups (the Assets groups, a host's extras) with every item's onInsert hook bound to the
 * composer it inserts into: `attachImage(dataUrl) -> bool` and `notify(text)`. Empty groups are dropped.
 */
export const withPromptInsertHooks = (groups, { attachImage, notify }) => (Array.isArray(groups) ? groups : [])
  .filter((g) => g && Array.isArray(g.items) && g.items.length > 0)
  .map((g) => ({
    ...g,
    items: g.items.map((it) => (typeof it?.onInsert === 'function'
      ? { ...it, onInsert: () => it.onInsert({ attachImage, notify }) }
      : it)),
  }));
const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const isWorkflowEntityToken = (token) => /^\{\{\s*workflowEntity\./.test(safeString(token));

/** Extracts the result rows from an executed query result, mirroring the server logic. */
const extractResultRows = (result) => {
  try {
    let value = result;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
      value = JSON.parse(trimmed);
    }
    if (value == null) return [];
    if (Array.isArray(value)) return value;
    if (Array.isArray(value.value)) return value.value;
    if (typeof value === 'object') return [value];
    return [];
  } catch {
    return [];
  }
};

/** The document-parameter declarations as an array ([] for blank or malformed JSON). */
export const parseDocumentParams = (json) => {
  const text = safeString(json).trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// Bare localization keys (e.g. the AI usage limit) are translated; arbitrary error prose
// is left alone so i18n lookup quirks cannot mangle it.
const LOCALIZATION_KEY_REGEX = /^application_strings(\.[\w-]+)+$/;

const friendlyError = (raw) => {
  const s = safeString(raw).trim();
  if (!s) return '';
  try {
    const jsonStart = s.indexOf('{');
    if (jsonStart >= 0) {
      const parsed = JSON.parse(s.substring(jsonStart));
      const title = safeString(parsed?.title).trim();
      if (title) return title;
    }
  } catch { /* not JSON */ }
  return LOCALIZATION_KEY_REGEX.test(s) ? t(s) : s;
};

/**
 * Version history for several named artifacts (each query plus each output), so chat
 * messages can carry restorable per-artifact version chips.
 */
const useArtifactVersions = () => {
  const ref = React.useRef({});

  const pushVersion = React.useCallback((key, content, label, extra) => {
    const text = safeString(content);
    if (!text) return null;
    const list = ref.current[key] ?? [];
    const latest = list[list.length - 1];
    if (latest && latest.content === text) {
      // Same content, fresher execution data (e.g. an output re-run on new rows):
      // refresh the entry's extras so restoring it re-renders the latest result.
      if (extra && Object.values(extra).some((v) => v != null && v !== '')) {
        const refreshed = { ...latest, ...extra };
        ref.current = { ...ref.current, [key]: [...list.slice(0, -1), refreshed] };
        return refreshed;
      }
      return latest;
    }
    const entry = {
      id: makeId(),
      key,
      seq: (latest?.seq ?? 0) + 1,
      content: text,
      label: safeString(label),
      ...(extra ?? {}),
    };
    ref.current = { ...ref.current, [key]: [...list, entry] };
    return entry;
  }, []);

  const getVersion = React.useCallback((id) => {
    for (const list of Object.values(ref.current)) {
      const found = list.find((v) => v.id === id);
      if (found) return found;
    }
    return null;
  }, []);

  const getVersionList = React.useCallback((key) => ref.current[key] ?? [], []);

  return { pushVersion, getVersion, getVersionList };
};

const sectionStyle = { border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, display: 'grid', gap: 8 };
const statusStyle = {
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const TestOutcomeBadge = ({ ok, error, edited }) => {
  // A hand-edited script's last verdict belongs to the text it replaced: say so instead of
  // showing nothing (or, worse, the stale pass the edit invalidated).
  if (ok == null) {
    return edited
      ? <span style={{ color: '#b26a00', fontSize: 11, fontWeight: 700 }}>{t('Edited since the last test')}</span>
      : null;
  }
  if (ok) return <span style={{ color: '#2e7d32', fontSize: 11, fontWeight: 700 }}>{t('Test passed')}</span>;
  return (
    <span style={{ color: '#b71c1c', fontSize: 11, fontWeight: 700, wordBreak: 'break-word' }}>
      {t('Test failed')}{error ? `: ${friendlyError(error)}` : ''}
    </span>
  );
};

/**
 * Combined AI assistant for N named queries plus N JavaScript output shapes. One
 * conversation drives all artifacts: the server executes each query, grounds the outputs
 * against the real result rows, and returns typed per-artifact updates and test outcomes.
 * With no queries it runs in outputs-only mode: no query panels, and the session authors
 * output expressions against the host context alone.
 *
 * Configuration is profile-driven: sessionProfile selects both the server-side profile
 * plugin (prompt grounding and shape testing — see QueryShapingSessionProfiles, C#) and the
 * client-side behavior defaults (shape kind, add-outputs/add-queries, labels — see
 * queryShapingSessionProfiles). Hosts pass sessionProfile plus DATA props; the behavior
 * props below exist only as overrides for genuinely host-specific needs (e.g. per-trigger
 * result bindings).
 *
 * existingQueries entries are { name, query, resultBinding?, resultShape? }. When the
 * profile allows adding queries (setVariableFromQuery), bindings are derived from the
 * query name as queries.<name>; otherwise each entry's declared binding (or the profile
 * default) is used.
 */
const QueryShapingAssistantDlgBody = ({
  onClose,
  hostLabel,
  sessionProfile = QUERY_SHAPING_DEFAULT_PROFILE,
  // SERVER-KEPT SESSION (set by the AccentQueryShapingAssistantDlg wrapper when the host
  // passes sessionHost): { info, document } as loaded — the row and the document the
  // dialog resumes from. initialSession is then the document's snapshot. Every turn
  // sends the row's key and the dialog's state; the state syncs back between turns.
  serverSession = null,
  // The normalised host group (normalizeSessionHost) the picker lists.
  sessionGroup = null,
  // Picker callbacks (the wrapper reloads and remounts this body): open another
  // session / start a new one; the header reports row changes (rename, status).
  onSwitchSession,
  onNewSession,
  onServerSessionInfoChange,
  existingQueries,
  existingOutputs,
  // The AI authors only in the latest API version. existingApiVersion is the version a
  // stored artifact's queries were authored for (definition.apiVersion); the session keeps
  // executing/saving with it until a query is changed, at which point the session is
  // authoring and switches to apiVersion (Latest). New artifacts are Latest from the start.
  apiVersion: authoringApiVersion = LATEST_API_VERSION,
  existingApiVersion,
  templateCatalog,
  javascriptTemplateCatalog,
  entityFieldCatalog,
  initialVariablesJson = '{}',
  initialTemplateValues,
  initialSessionOptions,
  runtimeNotes,
  shapeKind: shapeKindProp,
  allowAddOutputs: allowAddOutputsProp,
  allowAddQueries: allowAddQueriesProp,
  outputsLabel: outputsLabelProp,
  outputsHint: outputsHintProp,
  // Product change-review sessions (profile "productChangeReview"): the computed diff
  // entries of the edit under review (serialized DiffViewer entries), the edited
  // product's id (the server loads the saved definition itself — the client never
  // uploads it), and optionally the user's intent statement.
  changeDiffJson,
  intentStatement,
  productID,
  // Product explainer sessions (profile "productExplainer"): the configuration under
  // test. A FUNCTION is snapshotted on every send, so a resumable chat always reports
  // the host's current state; a string is sent as-is.
  explainContextJson,
  // Host-context sessions (profile "commissionRule"): the host editor's in-memory data
  // the assistant works on (for commissions: the focused rule, every rule and value,
  // the selectable commission dates). A FUNCTION is snapshotted on every send; a
  // string is sent as-is. The server enriches it (schema, functions, picklists,
  // legend) and renders it as its own budgeted prompt section.
  hostContextJson,
  // DOCUMENT PARAMETERS (profile.hasDocumentParams, e.g. pdfDocument): the print-time
  // parameter declarations the assistant may author alongside the script (server lane
  // DocumentParamsSessionArtifactLane). Round-trips on every request as
  // documentParamsJson; an updated set comes back on the response and is returned to
  // the host from OK as `documentParams` (the parsed array). A JSON array string.
  initialDocumentParamsJson,
  // HOST LANE ARTIFACTS (profile.hostLanes, e.g. supplyOrderIntegration's secret
  // declarations and sample replies): the host-declared lane artifacts keyed by lane
  // key, each a JSON string, exactly as the editor holds them. Round-trip on every
  // request as laneArtifacts; the lanes the turn changed come back on the response
  // (LaneArtifacts / LaneArtifactsUpdated) and the full set is returned to the host
  // from OK as `laneArtifacts`.
  initialLaneArtifacts,
  // DEPENDENT ASSETS (profile.supportsDependentAssets): the asset-library entries the
  // customisation is designed from ([{ key, purpose, designedFromHash }] or the JSON
  // array string the definition stores). Shown as the References row above the
  // composer, sent on every request as laneArtifacts.dependentAssets (the server reads
  // their content for the assistant; it never changes the list), and returned from OK
  // as `dependentAssets` with designedFromHash refreshed to the library's current
  // hashes — accepting the work means the design was checked against those copies.
  initialDependentAssets,
  // Fires with the normalised list whenever the references change DURING the session (an asset inserted from the
  // composer's Assets menu, the References row edited), so a host whose host context carries the declared assets
  // (the document template editor's assets[]) can keep it current for the session's test renders; OK returns the
  // final list regardless.
  onDependentAssetsChanged,
  // HOST PREVIEW SLOT: a React node (or array of { title, content } tabs) rendered in
  // the left pane INSTEAD of the default Preview/Artifacts tabs — hosts show their own
  // screens (the Changes diff, price details) beside the chat. When absent and the
  // profile is chat-only (no queries, no outputs), the left pane is hidden entirely.
  previewContent,
  // RESUMABLE SESSION: the session object a previous open returned in its onClose
  // payload ({ diagnosticSessionID, messages, templateValues, sessionOptions }).
  // Supplying it resumes the same conversation — same diagnostic id (server capture,
  // flow log and Send Chat as Bug stay correlated), same transcript, same options.
  // Every onClose payload carries the current session so hosts can hold it.
  initialSession,
  // SUGGESTED PROMPTS: shown as one-click chips above the composer while the transcript
  // is empty, so a blank artifact (a new document template, a new report) starts from
  // "Create a nice Quote document" rather than a blank prompt. Falls back to the
  // profile's static list; hosts pass a type-specific one (the server registry's).
  suggestedPrompts,
  // INITIAL PROMPT: sent as the first message as soon as the dialog opens (a host's
  // own suggested-prompt button opened the assistant with it). Ignored when the
  // session resumes an existing transcript - unless initialPromptIntoResumed, in which
  // case it is sent as the next message of that transcript (an editor's revised brief).
  // A server-kept dialog sends it on its first session only, never after a switch.
  initialPrompt,
  initialPromptIntoResumed = false,
  // INITIAL PROMPT INTENT: 'change' (the default - the host's prompt records or changes
  // something) or 'ask' (a report / review: the server answers read-only and never
  // retries the reply as an unhonored change).
  initialPromptIntent = 'change',
  // PROMPT-ONLY CATALOG EXTRAS: catalog groups ([{ group, items: [{ label, token, snippet,
  // onInsert? }] }]) added to the prompt composer's right-click menu only — never to the
  // query / script editors or the server's allowed-template lists. An item's onInsert
  // runs after its snippet landed with { attachImage(dataUrl) -> bool, notify(text) }, so
  // a host can declare what the reference stands for and attach its thumbnail to the
  // message (the document template editor's Option display group). The library's Assets /
  // Assets (Referenced) groups are the dialog's own for dependent-asset profiles.
  promptCatalogExtras,
  // Customization persistence: when true a Save button serializes the session's
  // artifacts as a Customization draft (customization = { id, name } binds the
  // session to an existing record; otherwise the first save prompts for a name and
  // creates one). onCustomizationSaved fires after every successful save.
  allowSaveAsCustomization = false,
  customization = null,
  onCustomizationSaved,
  // Customizations-page editing mode: the footer is just Save (saves the draft and
  // CLOSES the dialog) + the dialog's own Cancel. The workflow-host OK button
  // ("Use query and outputs") is meaningless here — there is no host editor
  // consuming the result — so it is not rendered.
  customizationEditor = false,
}) => {
  // Behavior defaults come from the client profile registry; an explicitly passed prop
  // (non-nullish) always wins so hosts can override one aspect without forking a profile.
  const profile = resolveQueryShapingProfile(safeString(sessionProfile));
  const shapeKind = shapeKindProp ?? profile.shapeKind;
  // Query-only profiles (hasOutputs: false) author ONLY the query: the outputs panel is
  // hidden, no output artifacts travel to the server, and the AI never proposes any.
  const hasOutputs = profile.hasOutputs !== false;
  const allowAddOutputs = hasOutputs && (allowAddOutputsProp ?? profile.allowAddOutputs);
  // Whether the ASSISTANT may introduce outputs this session: the user's add
  // capability, or a profile whose artifact names are a contract the user never types
  // but the assistant extends (commission values). Sent as allowNewShapes.
  const aiMayAddOutputs = hasOutputs && (allowAddOutputs || profile.aiMayAddOutputs === true);
  const allowAddQueries = allowAddQueriesProp ?? profile.allowAddQueries ?? false;
  // Preview snip is on unless the profile opts out (adhocReport: sandboxed iframe preview).
  const supportsSnip = profile.supportsSnip !== false;
  // Plan-first profiles: the assistant ships a plan the user approves in the Plan
  // panel before any artifact is accepted (server lane PlanSessionArtifactLane).
  const hasPlan = profile.hasPlan === true;
  const outputsLabel = outputsLabelProp ?? (profile.outputsLabel ? t(profile.outputsLabel) : '');
  const outputsHint = outputsHintProp ?? (profile.outputsHint ? t(profile.outputsHint) : '');

  const [queries, setQueries] = React.useState(() => (Array.isArray(existingQueries) ? existingQueries : [])
    .filter((q) => q)
    .map((q) => ({
      id: makeId(),
      name: safeString(q.name).trim() || 'query',
      query: safeString(q.query),
      resultBinding: safeString(q.resultBinding ?? profile.resultBinding),
      resultShape: q.resultShape ?? profile.resultShape,
      // Data export sessions: the mapped column whose distinct values fan this sheet
      // out into one workbook tab per value at generate time ('' = single tab).
      splitByColumn: safeString(q.splitByColumn).trim(),
      editorKey: 0,
      lastResult: null,
    })));
  // Query texts as loaded, by id: the session stays on the artifact's stored API version
  // until one of them changes (or a query is added/removed).
  const baselineQueriesRef = React.useRef(null);
  if (baselineQueriesRef.current === null) {
    baselineQueriesRef.current = new Map(queries.map((q) => [q.id, q.query]));
  }
  const storedApiVersion = normalizeApiVersion(existingApiVersion, null);
  const queriesTouched = !storedApiVersion ? false : (
    queries.length !== baselineQueriesRef.current.size
    || queries.some((q) => !baselineQueriesRef.current.has(q.id) || baselineQueriesRef.current.get(q.id) !== q.query)
  );
  const apiVersion = storedApiVersion && !queriesTouched ? storedApiVersion : authoringApiVersion;
  const [outputs, setOutputs] = React.useState(() => (Array.isArray(existingOutputs) ? existingOutputs : [])
    .map((o) => ({
      id: makeId(),
      name: safeString(o?.name).trim(),
      script: safeString(o?.script),
      editorKey: 0,
      testOk: null,
      error: '',
      valueJson: '',
    })));
  const [templateValues, setTemplateValues] = React.useState(() => ({
    ...(initialTemplateValues ?? {}),
    // A resumed session's collected values win: they include test values the user
    // entered during the earlier open.
    ...(initialSession?.templateValues ?? {}),
  }));
  // Bumped when a suggestion chip fills a template value, so the (uncontrolled) template
  // inputs remount and show the applied value without losing focus during normal typing.
  const [templateEpoch, setTemplateEpoch] = React.useState(0);
  const [lastPromptTokens, setLastPromptTokens] = React.useState([]);
  const [status, setStatus] = React.useState(STATUS_IDLE);
  // Pipeline usage of the most recent turn (AI model calls / data API calls), shown as a
  // tiny hover hint so the cost of a turn is visible without cluttering the chat.
  const [lastTurnUsage, setLastTurnUsage] = React.useState(null);
  const sessionUsageRef = React.useRef({ ai: 0, data: 0 });
  // Per-session user choices interpreted by the profile plugin (e.g. the ad-hoc report
  // third-party-libraries consent). Sent on every chat request as sessionOptions: the
  // server profile turns them into prompt rules and artifact validators.
  const [sessionOptions, setSessionOptions] = React.useState({
    allowExternalLibraries: false,
    ...(initialSessionOptions ?? {}),
    ...(initialSession?.sessionOptions ?? {}),
  });
  // The change review's CHECKLIST artifact (profile.hasChecklist), grouped by logical
  // change: the ORACLE owns the groups and item wording (server-merged by item id),
  // the USER owns each item's state, ticked in the checklist tab. Round-trips on
  // every chat request and rides the session object across close/reopen.
  const [checklistGroups, setChecklistGroups] = React.useState(
    () => (profile.hasChecklist ? (parseChecklistGroups(initialSession?.checklistJson) ?? []) : []),
  );
  // The intent collected from the user's intake-question answers (one line per
  // answered question, accumulating across rounds). Sent as (part of) the request's
  // intentStatement so the oracle reviews against it; rides the session for resume.
  const [collectedIntent, setCollectedIntent] = React.useState(
    () => safeString(initialSession?.collectedIntent),
  );
  // Plan-first sessions (profile.hasPlan): the PLAN artifact as the server last
  // shipped it ({ goal, steps: [{ id, kind, title, detail, status }] }) and whether the
  // user approved THIS revision. Both round-trip on every request (the server rejects
  // output artifacts until approval) and ride the session for resume; a revised plan
  // resets the approval.
  const [plan, setPlan] = React.useState(() => (hasPlan ? (parsePlan(initialSession?.planJson) ?? null) : null));
  const [planApproved, setPlanApproved] = React.useState(() => (hasPlan ? !!initialSession?.planApproved : false));
  // Document-parameter sessions (profile.hasDocumentParams): the declarations as the
  // server last shipped them (or as the host supplied them), a JSON array string. A
  // resumed session's copy wins over the host's initial value.
  const hasDocumentParams = profile.hasDocumentParams === true;
  const [documentParamsJson, setDocumentParamsJson] = React.useState(() => (hasDocumentParams
    ? safeString(initialSession?.documentParamsJson ?? initialDocumentParamsJson).trim()
    : ''));
  // Host-lane sessions (profile.hostLanes): the lane artifacts keyed by lane key, each
  // a JSON string, as the server last shipped them (or as the host supplied them). A
  // resumed session's copy wins over the host's initial value.
  const hostLanes = React.useMemo(() => (Array.isArray(profile.hostLanes) ? profile.hostLanes : []), [profile.hostLanes]);
  const hasHostLanes = hostLanes.length > 0;
  const [laneArtifacts, setLaneArtifacts] = React.useState(() => {
    if (!hasHostLanes) return {};
    const source = initialSession?.laneArtifacts ?? initialLaneArtifacts ?? {};
    const result = {};
    hostLanes.forEach((lane) => {
      const value = source?.[lane.key];
      result[lane.key] = typeof value === 'string' ? value : (value == null ? '[]' : JSON.stringify(value));
    });
    return result;
  });
  // Dependent-asset sessions (profile.supportsDependentAssets): the references the
  // customisation is designed from, user-curated in the References row. A resumed
  // session's list is kept (its entries keep their purpose / designedFromHash) and anything the
  // host declared since - e.g. in the brief wizard before sending the brief into this session -
  // joins it; normalizeDependentAssets dedupes by key, first occurrence winning.
  const hasDependentAssets = profile.supportsDependentAssets === true;
  const [dependentAssets, setDependentAssets] = React.useState(() => (hasDependentAssets
    ? normalizeDependentAssets([
      ...normalizeDependentAssets(initialSession?.dependentAssets),
      ...normalizeDependentAssets(initialDependentAssets),
    ])
    : []));
  // The main panel is tabbed: 'artifacts' (query/output editors) and 'preview' (the
  // profile-owned preview component). Profiles pick where users land.
  const [activeTab, setActiveTab] = React.useState(profile.defaultTab === 'preview' ? 'preview' : 'artifacts');
  // Whether the most recent chat turn's automated review reported defects. Restoring a
  // version does not re-run the review, so the restore handler uses this to warn that
  // the last verdict has not been re-checked against the restored artifacts. The warn
  // fires ONCE per review (not per click): a run of restore clicks must not wallpaper
  // the chat with the same note.
  const lastReviewFailedRef = React.useRef(false);
  const reviewNoteShownRef = React.useRef(false);
  // Server context warnings repeat identically every turn until fixed; only announce
  // them when they change so they inform without flooding the chat.
  const lastContextWarningsRef = React.useRef('');

  // Client half of the profile's context contract: warn developers the moment a host
  // stops passing an expected prop, instead of the AI silently degrading. (The server
  // validates the same contract per turn and returns user-visible ContextWarnings.)
  React.useEffect(() => {
    const provided = {
      templateCatalog: Array.isArray(templateCatalog) && templateCatalog.length > 0,
      javascriptTemplateCatalog: Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0,
      runtimeNotes: Array.isArray(runtimeNotes) && runtimeNotes.length > 0,
      changeDiffJson: !!safeString(changeDiffJson).trim(),
      intentStatement: !!safeString(intentStatement).trim(),
      productID: productID !== undefined && productID !== null,
      explainContextJson: typeof explainContextJson === 'function' || !!safeString(explainContextJson).trim(),
      hostContextJson: typeof hostContextJson === 'function' || !!safeString(hostContextJson).trim(),
    };
    const missing = (profile.expectedContext ?? []).filter((name) => !provided[name]);
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `AccentQueryShapingAssistantDlg (${profile.key}): host did not provide expected context: ${missing.join(', ')}. ` +
        'The AI session runs degraded; fix the host editor to pass these props.');
    }
    // Contract check belongs to dialog open, not to prop churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    chatMessages,
    setChatMessages,
    pushMsg,
    buildServerMessages,
  } = useAiChatAssistant({});
  const { pushVersion, getVersion, getVersionList } = useArtifactVersions();
  // the prompt composer (AccentPromptChat ref): imperative send / addImage
  const chatPromptRef = React.useRef(null);

  // Resume a previous open's transcript: the host held the session object across a
  // close/reopen, so the conversation continues where it left off.
  React.useEffect(() => {
    if (Array.isArray(initialSession?.messages) && initialSession.messages.length > 0) {
      setChatMessages(initialSession.messages);
    }
    // Seeding belongs to dialog open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One id per SESSION: every chat turn sends it so the server can correlate its
  // diagnostic capture with this session for "Send Chat as Bug". A resumed session
  // reuses its id, so the server-side capture, flow log and cached definitions stay
  // correlated across close/reopen.
  // A server-kept session's id is the row's (qs_{key}): the server sets it on every turn
  // of the session, so the capture is one per session however many times it is resumed.
  const diagnosticSessionIdRef = React.useRef(
    safeString(serverSession?.document?.diagnosticSessionID).trim()
    || safeString(initialSession?.diagnosticSessionID).trim()
    || `qs_${makeId()}`,
  );
  const serverSessionKey = safeString(serverSession?.info?.sessionKey).trim();
  // The state sync's bookkeeping (see "state sync between turns" below): the timer, the
  // last JSON written, and whether the first (as-opened) state has been seen.
  const snapshotSyncRef = React.useRef({ timer: null, lastJson: null, armed: false });
  // The row as the header shows it; the header's own writes (rename, archive) update it.
  const [serverSessionInfo, setServerSessionInfo] = React.useState(serverSession?.info ?? null);
  const onSessionInfoChange = React.useCallback((info) => {
    setServerSessionInfo(info);
    onServerSessionInfoChange?.(info);
  }, [onServerSessionInfoChange]);
  // True while the server reports a turn running for this session that THIS dialog did
  // not start (it was closed mid-turn and reopened): the composer waits and the dialog
  // polls for the outcome.
  const [serverTurnRunning, setServerTurnRunning] = React.useState(!!serverSession?.info?.turnInProgress);

  const busy = status !== STATUS_IDLE || serverTurnRunning;
  const hasQueries = queries.length > 0;

  // A reference attached mid-session is announced in the chat: the assistant sees it
  // from the next request (the lane reads the list every turn), so the user learns not
  // to paste what the library now holds.
  const onDependentAssetAdded = React.useCallback((asset, purpose) => {
    pushMsg('system', purpose === 'runtime'
      ? t('"{{key}}" attached as a runtime asset. The assistant sees it from the next message on.', { key: asset.Key })
      : t('"{{key}}" attached as a design reference. The assistant reads it from the next message on; you do not need to paste its content.', { key: asset.Key }));
  }, [pushMsg]);

  // "Save as reference" proposals the user dismissed this session (asset keys): sent on
  // every request so the assistant does not propose them again, and kept on the session
  // across close/reopen.
  const [dismissedAssetProposals, setDismissedAssetProposals] = React.useState(() => (
    Array.isArray(initialSession?.dismissedAssetProposals) ? initialSession.dismissedAssetProposals.map(safeString).filter(Boolean) : []
  ));

  // Composer pre-filter: a draft that looks like documentation (long, or structured
  // JSON/YAML/CSV) raises the library hint. Text only: a pasted image is a screenshot
  // and belongs to the turn.
  const [pasteHint, setPasteHint] = React.useState(false);
  const onComposerDraftChange = React.useCallback((draft) => {
    setPasteHint(looksLikeLibraryMaterial(draft));
  }, []);

  // SESSION FILES: what the user pastes, drops or attaches lives on the server-kept
  // session (chips under the composer; the assistant reads them through its kit). A
  // chip kept as a reference joins the design references like a library upload would.
  const onSessionFileKept = React.useCallback((saved) => {
    if (hasDependentAssets && saved?.Key) {
      setDependentAssets((prev) => {
        const next = normalizeDependentAssets(prev).filter((a) => a.key !== saved.Key);
        next.push({
          key: saved.Key,
          purpose: 'reference',
          ...(saved.ContentHash ? { designedFromHash: safeString(saved.ContentHash).toLowerCase() } : {}),
        });
        return next;
      });
    }
    onDependentAssetAdded(saved, 'reference');
  }, [hasDependentAssets, onDependentAssetAdded]);
  const sessionFiles = useAssistantSessionDocuments({ sessionKey: serverSessionKey, onKept: onSessionFileKept, notify: pushMsg });
  const hasSessionFiles = sessionFiles.enabled;
  // The turn handler reads the hook through a ref: it is memoised on many things already.
  const sessionFilesRef = React.useRef(sessionFiles);
  sessionFilesRef.current = sessionFiles;

  // A drag over anywhere in the dialog lights the composer's drop zone; a drop anywhere
  // lands on the session (the composer handles drops on itself).
  const [dialogDragOver, setDialogDragOver] = React.useState(false);
  const dialogDragDepthRef = React.useRef(0);
  const dialogDragHandlers = React.useMemo(() => {
    if (!hasSessionFiles) return {};
    const isFileDrag = (e) => {
      try { return Array.from(e?.dataTransfer?.types ?? []).includes('Files'); } catch { return false; }
    };
    return {
      onDragEnter: (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        dialogDragDepthRef.current += 1;
        setDialogDragOver(true);
      },
      onDragOver: (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
      },
      onDragLeave: (e) => {
        if (!isFileDrag(e)) return;
        dialogDragDepthRef.current = Math.max(0, dialogDragDepthRef.current - 1);
        if (dialogDragDepthRef.current === 0) setDialogDragOver(false);
      },
      onDrop: (e) => {
        dialogDragDepthRef.current = 0;
        setDialogDragOver(false);
        if (!isFileDrag(e) || e.isDefaultPrevented?.() || e.defaultPrevented) return;
        e.preventDefault();
        if (!busy) sessionFiles.attachFiles(e.dataTransfer.files);
      },
    };
  }, [busy, hasSessionFiles, sessionFiles]);

  /**
   * The ctx path a query's rows bind at. Add-queries profiles derive it from the query
   * name (queries.<name>, matching the executor); fixed-query hosts declare it per entry.
   */
  const bindingFor = React.useCallback((q) => (
    allowAddQueries ? setVariableFromQueryBindingPath(q.name) : safeString(q.resultBinding).trim()
  ), [allowAddQueries]);

  // Query snippet literals follow the profile's dialect. SQL additionally quotes the
  // product ProductID tokens: their bare-guid form is an OData guid literal, which is
  // invalid inside SQL text.
  const queryCatalog = React.useMemo(() => {
    const base = Array.isArray(templateCatalog) ? templateCatalog : [];
    if (profile.queryLanguage === 'sql') {
      return mapCatalogGroup(mapFixedPicklists(base, toSqlStringLiteral), PRODUCTS_TEMPLATE_GROUP, toSqlStringLiteral);
    }
    return mapFixedPicklists(base, toODataStringLiteral);
  }, [profile.queryLanguage, templateCatalog]);
  const jsCatalogBase = React.useMemo(() => (
    Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0
      ? javascriptTemplateCatalog
      : (Array.isArray(templateCatalog) ? templateCatalog : [])
  ), [javascriptTemplateCatalog, templateCatalog]);
  // Product group items carry raw ProductID guids; each editor maps its literal form:
  // the OData editor keeps the bare guid (guid literals are unquoted), the JS editor
  // quotes it, and the prompt editor inserts "Name (ProductID guid)".
  const jsCatalog = React.useMemo(() => buildJavascriptMenuCatalog(jsCatalogBase), [jsCatalogBase]);
  // Dependent-asset sessions offer the asset library in the prompt menu ("Design
  // references"): inserting asset:<key> declares the asset as a reference when it is not
  // one yet, so the lane reads it from the next turn on. The library is a snapshot taken
  // when the dialog opens (the shared 15s cache); the declared list is read live.
  const dependentAssetsRef = React.useRef(dependentAssets);
  const onDependentAssetsChangedRef = React.useRef(onDependentAssetsChanged);
  onDependentAssetsChangedRef.current = onDependentAssetsChanged;
  React.useEffect(() => {
    const changed = dependentAssetsRef.current !== dependentAssets;
    dependentAssetsRef.current = dependentAssets;
    if (changed && hasDependentAssets && typeof onDependentAssetsChangedRef.current === 'function') {
      onDependentAssetsChangedRef.current(dependentAssets);
    }
  }, [dependentAssets, hasDependentAssets]);
  const [assetLibrary, setAssetLibrary] = React.useState(null);
  React.useEffect(() => {
    if (!hasDependentAssets) return undefined;
    let alive = true;
    loadAssetLibrary().then((list) => { if (alive) setAssetLibrary(Array.isArray(list) ? list : []); }).catch(() => { if (alive) setAssetLibrary([]); });
    return () => { alive = false; };
  }, [hasDependentAssets]);
  // "Assets" (not yet referenced) and "Assets (Referenced)" (this customization's dependentAssets): rebuilt as the
  // references change so an inserted asset moves across on the next right-click. Inserting asset:<key> into the
  // prompt means "use this": where the host embeds assets at run time (document templates) the entry is a runtime
  // asset, otherwise a design reference.
  const referenceCatalogGroups = React.useMemo(() => {
    if (!hasDependentAssets || !Array.isArray(assetLibrary) || assetLibrary.length === 0) return [];
    const purpose = profile.dependentAssetsAllowRuntime === true ? 'runtime' : 'reference';
    return buildDependentAssetPromptCatalog(assetLibrary, {
      referenced: dependentAssets,
      declare: (asset) => {
        const key = safeString(asset?.Key).trim();
        if (!key || dependentAssetsRef.current.some((a) => a.key === key)) return false;
        setDependentAssets((prev) => (prev.some((a) => a.key === key) ? prev : [...prev, { key, purpose }]));
        return true;
      },
    });
  }, [assetLibrary, dependentAssets, hasDependentAssets, profile.dependentAssetsAllowRuntime]);
  const promptTemplateCatalog = React.useMemo(() => {
    // Lookup groups (catalog groups carrying a lookupKey — reps, suppliers, statuses)
    // insert "Name (<lookupKey> <id>)" the same way the Products group does.
    const catalog = buildPromptMenuCatalog(jsCatalog);
    const withEntity = entityFieldCatalog ? [...catalog, entityFieldCatalog] : catalog;
    // host extras (and the session's own Assets / Assets (Referenced) groups) are prompt-only; their
    // onInsert hooks get the composer's attach + the transcript notifier
    const extras = withPromptInsertHooks(
      [...referenceCatalogGroups, ...(Array.isArray(promptCatalogExtras) ? promptCatalogExtras : [])],
      {
        attachImage: (dataUrl) => !!chatPromptRef.current?.addImage?.({ dataUrl }),
        notify: (text) => pushMsg('system', safeString(text)),
      }
    );
    return extras.length > 0 ? [...withEntity, ...extras] : withEntity;
  }, [entityFieldCatalog, jsCatalog, promptCatalogExtras, pushMsg, referenceCatalogGroups]);
  // Whether the right-click menu offers resolved reference-data insertions (lookup
  // groups) — drives the hint under the prompt box.
  const hasLookupReferences = React.useMemo(
    () => hasLookupReferenceGroups(promptTemplateCatalog),
    [promptTemplateCatalog]
  );
  // The intake card's "Other" box gets the prompt's menu minus the hook-carrying groups
  // (their onInsert targets the main composer's image attachments).
  const intakeTemplateCatalog = React.useMemo(
    () => promptTemplateCatalog.filter((g) => !(Array.isArray(g?.items) && g.items.some((it) => typeof it?.onInsert === 'function'))),
    [promptTemplateCatalog]
  );
  const queryAllowedTemplates = React.useMemo(() => collectCatalogTokens(queryCatalog), [queryCatalog]);
  // Tokens MUST be collected from the RAW catalog: normalizeJavascriptCatalog rewrites
  // {{vars.x}} tokens into ctx.vars.x snippet form, which the handlebars token collector
  // then drops — leaving the server's allowed-template list AND its derived "Known ctx
  // fields" prompt section empty. (Shipped once; the server's context validator now
  // flags this pipeline break as a warning too.) JS catalogs may carry either handlebars
  // tokens or ready-made ctx.* paths (custom validation uses the latter) — accept both.
  const shapeAllowedTemplates = React.useMemo(
    () => collectCatalogTokens(jsCatalogBase, (token) => isHandlebarsToken(token) || isContextPathToken(token)),
    [jsCatalogBase]
  );

  const usedTemplates = React.useMemo(() => {
    const tokens = [];
    for (const q of queries) {
      tokens.push(...extractHandlebarsTokens(q.query));
      const missing = q.lastResult?.MissingTemplates ?? q.lastResult?.missingTemplates ?? [];
      if (Array.isArray(missing)) tokens.push(...missing);
    }
    if (Array.isArray(lastPromptTokens)) tokens.push(...lastPromptTokens);
    // Server-owned tokens (current user) resolve on the server every run — an input
    // here would suggest the value is the user's to choose (it is not).
    return Array.from(new Set(tokens)).filter((token) => !!token && !isServerOwnedTemplateToken(token));
  }, [lastPromptTokens, queries]);

  // Hosts without a workflow entity (timer triggers run before any workflow entity
  // exists) carry no workflowEntity.* tokens in their catalog. A query holding one
  // would still test fine here — the token becomes a fillable test value — yet resolve
  // to BLANK at the real runtime, so call it out the moment such a token shows up.
  const warnedWorkflowEntityTokensRef = React.useRef(new Set());
  React.useEffect(() => {
    if (queryAllowedTemplates.some(isWorkflowEntityToken)) return;
    const offending = usedTemplates.filter((token) => isWorkflowEntityToken(token)
      && !warnedWorkflowEntityTokensRef.current.has(token));
    if (offending.length === 0) return;
    for (const token of offending) warnedWorkflowEntityTokensRef.current.add(token);
    pushMsg('error',
      `${t('The workflow entity is not available in this context, so these tokens will resolve to blank when the workflow runs')}: ${offending.join(', ')}. ${t('Remove them and select the rows another way (e.g. by ID or status).')}`);
  }, [pushMsg, queryAllowedTemplates, usedTemplates]);

  /**
   * First-class locals for output tests: every query's latest result rows bound at its
   * declared ctx path (plus a ".count" sibling for ".rows" bindings). Locals are the
   * same channel the engine and the server-side session use — the shared JS harness
   * overlays them onto ctx LAST, so fresh rows always beat any stale template value or
   * variable snapshot with the same name. Never inject result rows via template values.
   */
  const buildOutputTestLocals = React.useCallback(() => {
    const locals = {};
    for (const q of queries) {
      const binding = bindingFor(q);
      if (!binding) continue;
      const rows = extractResultRows(q.lastResult?.Result ?? q.lastResult?.result);
      locals[binding] = rows.slice(0, 100);
      if (binding.endsWith('.rows')) {
        locals[`${binding.slice(0, -'.rows'.length)}.count`] = rows.length;
      }
    }
    return locals;
  }, [bindingFor, queries]);

  const updateQuery = React.useCallback((id, patch) => {
    setQueries((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, []);

  const updateOutput = React.useCallback((id, patch) => {
    setOutputs((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  }, []);

  // A hand edit of an output script invalidates its last verdict: the pass (or failure)
  // belonged to the text that was replaced. The output is marked edited so the badge says
  // so and Use Script asks before handing over an untested edit.
  const onOutputScriptEdited = React.useCallback((id, script) => {
    setOutputs((prev) => prev.map((o) => {
      if (o.id !== id) return o;
      if (safeString(o.script) === script) return o;
      return { ...o, script, testOk: null, error: '', valueJson: '', edited: true };
    }));
  }, []);

  // The preview's render IS the test for hosts whose outputs are tested by rendering
  // (the PDF document): a failed render marks the output failed so the badge and the
  // Use Script guard see it; a successful render clears only a hand edit's unknown state
  // (a server-side verdict on an AI version - e.g. an undeclared parameter read on a
  // document that renders fine - stands).
  const onOutputRendered = React.useCallback((name, ok, error) => {
    const key = safeString(name).trim().toLowerCase();
    setOutputs((prev) => prev.map((o) => {
      if (safeString(o.name).trim().toLowerCase() !== key) return o;
      if (!ok) return { ...o, testOk: false, error: safeString(error), edited: false };
      return o.edited ? { ...o, testOk: true, error: '', edited: false } : o;
    }));
  }, []);

  /** A short display label for a query artifact ("Query" while there is only one). */
  const queryLabel = React.useCallback((q) => (
    queries.length > 1 || allowAddQueries ? (q.name || t('Query')) : t('Query')
  ), [allowAddQueries, queries.length]);

  const announceQueryResult = React.useCallback((label, queryResult) => {
    if (!queryResult) return;
    const testOk = !!(queryResult.TestOk ?? queryResult.testOk);
    const errorMsg = safeString(queryResult.Error ?? queryResult.error);
    const errCode = safeString(queryResult.ErrorCode ?? queryResult.errorCode).toLowerCase();
    const rowCount = queryResult.ValueCount ?? queryResult.valueCount;

    if (testOk) {
      pushMsg('system', rowCount === 0
        ? `${label}: ${t('test passed but returned no rows — check that filter values match real data.')}`
        : `${label}: ${t('test passed.')}`);
    } else if (errCode === 'missingtemplatevalues') {
      const tokens = queryResult.MissingTemplates ?? queryResult.missingTemplates ?? [];
      pushMsg('system', `${label}: ${t('enter test values for')} ${tokens.join(', ')} ${t('below, then test again.')}`);
    } else if (errorMsg) {
      pushMsg('error', `${label}: ${t('test failed')} — ${friendlyError(errorMsg)}`);
    }
  }, [pushMsg]);

  // The session object every close path returns: hosts that keep it and pass it back
  // via initialSession let the user close the chat, work in the real screens, and
  // resume the same conversation (same diagnostic id, same transcript). It is also
  // the state a server-kept session stores (sent with every turn, synced between
  // turns), so a resume from the server seeds exactly what initialSession seeds.
  const buildSession = React.useCallback((overrides) => ({
    diagnosticSessionID: diagnosticSessionIdRef.current,
    messages: chatMessages,
    templateValues,
    sessionOptions,
    // The review checklist (groups + the user's tick states) survives close/reopen
    // with the transcript; undefined for profiles without the checklist lane.
    ...(profile.hasChecklist && checklistGroups.length > 0
      ? { checklistJson: serializeChecklistGroups(checklistGroups) }
      : {}),
    // The intent collected from intake answers survives too, so a resumed session's
    // requests keep judging against it.
    ...(safeString(collectedIntent).trim() ? { collectedIntent } : {}),
    // The plan and its approval survive close/reopen so a resumed session keeps the
    // gate exactly where it was (approved plans stay approved).
    ...(hasPlan && plan ? { planJson: serializePlan(plan), planApproved } : {}),
    // The document parameters survive close/reopen so a resumed session keeps the
    // declarations the assistant authored even if the host has not accepted them yet.
    ...(hasDocumentParams && documentParamsJson ? { documentParamsJson } : {}),
    // Host-lane artifacts survive close/reopen for the same reason.
    ...(hasHostLanes ? { laneArtifacts } : {}),
    // The references attached during the session survive close/reopen too, as do the
    // "save as reference" proposals the user declined.
    ...(hasDependentAssets ? { dependentAssets, dismissedAssetProposals } : {}),
    ...(overrides ?? {}),
  }), [chatMessages, templateValues, sessionOptions, profile.hasChecklist, checklistGroups, collectedIntent, hasPlan, plan, planApproved, hasDocumentParams, documentParamsJson, hasHostLanes, laneArtifacts, hasDependentAssets, dependentAssets, dismissedAssetProposals]);

  // collectedIntentOverride: the intake submit handler updates the collected intent
  // and sends the turn in the same tick — state has not re-rendered yet, so the fresh
  // value rides as an explicit override (the SAME turn runs the review against it).
  // planApprovedOverride: likewise for the plan Approve button, which flips the
  // approval and sends the execute turn in one go.
  // messages: the transcript including the message being sent (AccentPromptChat passes
  // it; state has not re-rendered yet), snapshotted with a server-kept session's turn.
  // resumeResponse: a response the server produced for this session while the dialog
  // was closed (the session document's last turn) — applied exactly as a live turn's
  // response is, with no request sent.
  const onChatPrompt = React.useCallback(async ({ text, history, intent, collectedIntentOverride, planApprovedOverride, images, messages, resumeResponse }) => {
    try {
      setLastPromptTokens(extractHandlebarsTokens(text));

      // Record manual edits as versions so restore points stay coherent. Keep the
      // pre-turn entry per artifact: it becomes the restorable "previous" chip when
      // the AI changes that artifact this turn.
      const preTurn = {};
      for (const q of queries) {
        preTurn[`query:${q.id}`] = pushVersion(`query:${q.id}`, q.query, queryLabel(q));
      }
      for (const o of outputs) {
        // Versions carry the script's last EXECUTED value too, so restoring an output
        // re-renders the preview (e.g. the ad-hoc report HTML) without re-running anything.
        if (o.name) preTurn[`output:${o.id}`] = pushVersion(`output:${o.id}`, o.script, o.name, { valueJson: safeString(o.valueJson) });
      }

      // Superseded versions (excluding the current content) so the AI can reference
      // or restore earlier artifacts the user mentions.
      const priorContents = (key) => getVersionList(key).slice(0, -1).slice(-5).map((v) => v.content);

      setStatus(STATUS_GENERATING);
      // Shared envelope + send (see assistantChatApi.js): handled 500s throw their
      // translated message so the catch below reports them as an error turn.
      const res = resumeResponse ?? await sendAssistantChatTurn({
        // Server-kept session: the row's key and the dialog's state as it stands (the
        // transcript including this message), so the server runs the turn under the
        // session's lease and a resume finds the state the turn was sent from.
        sessionKey: serverSessionKey || undefined,
        sessionSnapshotJson: serverSessionKey
          ? JSON.stringify(buildSession(Array.isArray(messages) ? { messages } : undefined))
          : undefined,
        prompt: safeString(text),
        messages: buildServerMessages(history, text),
        queries: queries.map((q) => ({
          name: q.name,
          query: safeString(q.query),
          expectedResultShape: q.resultShape,
          resultBinding: bindingFor(q),
          priorVersions: priorContents(`query:${q.id}`),
          // Round-trip the server's "automatic repair gave up on this text" flag so the
          // next turn steers the AI toward a structurally different query. The server
          // clears it itself as soon as the query passes again.
          repairExhausted: !!(q.lastResult?.RepairExhausted ?? q.lastResult?.repairExhausted),
          splitByColumn: safeString(q.splitByColumn).trim() || undefined,
        })),
        shapes: outputs
          .filter((o) => !!o.name)
          .map((o) => ({
            name: o.name,
            script: safeString(o.script),
            kind: shapeKind,
            priorVersions: priorContents(`output:${o.id}`),
          })),
        allowNewShapes: aiMayAddOutputs,
        allowNewQueries: allowAddQueries,
        apiVersion,
        templateValues,
        queryAllowedTemplates,
        shapeAllowedTemplates,
        variablesJson: safeString(initialVariablesJson) || '{}',
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        hostLabel: safeString(hostLabel),
        diagnosticSessionID: diagnosticSessionIdRef.current,
        sessionProfile: safeString(sessionProfile),
        sessionOptions,
        // Explicit intent from the composer's action buttons ('ask' | 'change'); empty
        // when sent with Enter. 'ask' turns are server-enforced read-only.
        promptIntent: safeString(intent),
        // Screenshots pasted with this message (downscaled data URLs). The server
        // shows them to its authoring and review models as images; later turns carry
        // them on the history message (buildServerMessages forwards m.images).
        promptImages: Array.isArray(images) ? images : [],
        // Change-review context: the diff entries plus the edited product's id (the
        // server loads the saved "before" definition itself). Undefined for other hosts.
        changeDiffJson: safeString(changeDiffJson).trim() || undefined,
        // The host's own intent statement (if any) plus the intent collected from the
        // intake-question answers — first-class so review turns judge against it.
        intentStatement: [
          safeString(intentStatement).trim(),
          safeString(collectedIntentOverride ?? collectedIntent).trim(),
        ].filter(Boolean).join('\n') || undefined,
        changeProductID: productID ?? undefined,
        // Explainer context: snapshotted per SEND when the host passes a function, so
        // a resumed chat always sees the tester's current state.
        explainContextJson: (typeof explainContextJson === 'function'
          ? safeString(explainContextJson())
          : safeString(explainContextJson)).trim() || undefined,
        // Change-review checklist round-trip: the current groups INCLUDING the user's
        // tick states — the server preserves those by item id when a re-assessment
        // revises the content. Undefined for other profiles and before the first checklist.
        checklistJson: profile.hasChecklist && checklistGroups.length > 0
          ? serializeChecklistGroups(checklistGroups)
          : undefined,
        // Host context: snapshotted per SEND when the host passes a function, so the
        // assistant always works on the editor's current (unsaved) rules and values.
        hostContextJson: (typeof hostContextJson === 'function'
          ? safeString(hostContextJson())
          : safeString(hostContextJson)).trim() || undefined,
        // Plan-first round-trip: the current plan revision and whether the user has
        // approved it. The server gates output artifacts on the approval.
        planJson: hasPlan && plan ? serializePlan(plan) : undefined,
        planApproved: hasPlan ? (planApprovedOverride ?? planApproved) : undefined,
        // Document-parameter round-trip: the current declarations so the server's render
        // test and the assistant's prompt see what the script may read. Undefined for
        // other profiles; an empty array when none are declared yet.
        documentParamsJson: hasDocumentParams ? (documentParamsJson || '[]') : undefined,
        // Host-lane round-trip: every lane's current artifact so the server's tests and
        // the assistant's prompt see what is declared. Undefined for other profiles.
        // The dependent assets ride the same map under the dependentAssets lane key:
        // the server reads the referenced library entries for the assistant each turn.
        laneArtifacts: hasHostLanes || hasDependentAssets
          ? {
            ...(hasHostLanes ? laneArtifacts : {}),
            ...(hasDependentAssets ? { dependentAssets: serializeDependentAssets(dependentAssets) } : {}),
          }
          : undefined,
        // keys of "save as reference" proposals the user declined: not proposed again
        dismissedAssetProposals: hasDependentAssets && dismissedAssetProposals.length > 0 ? dismissedAssetProposals : undefined,
      });

      const message = safeString(res?.Message ?? res?.message);

      // The session files the assistant read this turn glow on their chips for a moment;
      // the list is refreshed too (a screenshot sent with the message is on the session now).
      const sessionFilesRead = res?.SessionFilesRead ?? res?.sessionFilesRead;
      if (Array.isArray(sessionFilesRead) && sessionFilesRead.length > 0) sessionFilesRef.current.markRead(sessionFilesRead);
      if (Array.isArray(images) && images.length > 0) sessionFilesRef.current.reload();

      // Host-lane turn result: the server ships only the lanes the turn changed
      // (LaneArtifactsUpdated); the client merges them over what it holds.
      if (hasHostLanes && (res?.LaneArtifactsUpdated ?? res?.laneArtifactsUpdated)) {
        const changed = res?.LaneArtifacts ?? res?.laneArtifacts;
        if (changed && typeof changed === 'object') {
          setLaneArtifacts((prev) => ({ ...prev, ...changed }));
          Object.keys(changed).forEach((key) => {
            const lane = hostLanes.find((l) => l.key === key || l.key.toLowerCase() === key.toLowerCase());
            let count = 0;
            try { count = JSON.parse(changed[key]).length; } catch { count = 0; }
            pushMsg('system', t('The {{label}} were updated ({{count}} declared) - they are written to the editor when you accept.', { label: lane?.label || key, count }));
          });
        }
      }
      const versionsForMessage = [];

      // Pipeline usage counters for this turn (every AI model call and data API call the
      // server made end-to-end, including repairs, probes and oracle rounds).
      const aiCallCount = Number(res?.AiCallCount ?? res?.aiCallCount ?? 0);
      const dataApiCallCount = Number(res?.DataApiCallCount ?? res?.dataApiCallCount ?? 0);
      if (aiCallCount > 0 || dataApiCallCount > 0) {
        sessionUsageRef.current = {
          ai: sessionUsageRef.current.ai + aiCallCount,
          data: sessionUsageRef.current.data + dataApiCallCount,
        };
        setLastTurnUsage({ ai: aiCallCount, data: dataApiCallCount });
      }

      // Review outcome and server notices arrive with the artifacts; a FAILED review
      // (concrete defects) gates every success indicator below — such a turn must never
      // present plain green chips/badges even when the machine tests passed. Advisory
      // review remarks are informational and gate nothing.
      const reviewNotes = (res?.ReviewNotes ?? res?.reviewNotes ?? []).map(safeString).filter(Boolean);
      const reviewFailed = !!(res?.ReviewFailed ?? res?.reviewFailed);
      const reviewAdvisories = (res?.ReviewAdvisories ?? res?.reviewAdvisories ?? []).map(safeString).filter(Boolean);
      const reviewScoreRaw = res?.ReviewScore ?? res?.reviewScore;
      const reviewScore = typeof reviewScoreRaw === 'number' ? reviewScoreRaw : null;
      const reviewConfidence = safeString(res?.ReviewConfidence ?? res?.reviewConfidence).toLowerCase();
      const reviewSummary = safeString(res?.ReviewSummary ?? res?.reviewSummary);
      // Notices are severity-typed on the server: an honestly-disclosed limitation must
      // not render in the same red as a hard failure next to a green review score.
      const notices = (res?.Notices ?? res?.notices ?? [])
        .map((n) => ({
          text: safeString(n?.Text ?? n?.text),
          severity: safeString(n?.Severity ?? n?.severity).toLowerCase(),
        }))
        .filter((n) => n.text);
      const gatedOk = (ok) => (reviewFailed ? null : ok);
      // Remembered so a later version restore can warn that the review has not re-checked
      // the restored artifacts. A fresh verdict re-arms the once-per-review restore note.
      lastReviewFailedRef.current = reviewFailed;
      reviewNoteShownRef.current = false;

      // Artifacts the assistant REMOVED this turn (the remove-directive protocol) —
      // explicit, because results are merged by name and mere absence from the response
      // is not a removal. Version history for removed artifacts stays restorable.
      const removedQueryNames = new Set((res?.RemovedQueries ?? res?.removedQueries ?? [])
        .map((n) => safeString(n).trim().toLowerCase())
        .filter(Boolean));
      const removedOutputNames = new Set((res?.RemovedOutputs ?? res?.removedOutputs ?? [])
        .map((n) => safeString(n).trim().toLowerCase())
        .filter(Boolean));

      // Query updates, matched to session queries by name.
      const queryResults = res?.Queries ?? res?.queries ?? [];
      let nextQueries = [...queries];
      for (const queryResult of queryResults) {
        const name = safeString(queryResult?.Name ?? queryResult?.name).trim();
        let ix = nextQueries.findIndex((q) => q.name.toLowerCase() === name.toLowerCase());
        if (ix < 0) {
          // A query the AI introduced this turn (multi-query strategy). Only add-queries
          // hosts accept these; the server enforces the same capability, so an unknown
          // name on a fixed-query host is simply ignored.
          const isNew = !!(queryResult?.New ?? queryResult?.new);
          if (!isNew || !allowAddQueries || !name) continue;
          nextQueries.push({
            id: makeId(),
            name,
            query: '',
            resultBinding: '',
            resultShape: profile.resultShape,
            splitByColumn: '',
            editorKey: 0,
            lastResult: null,
          });
          ix = nextQueries.length - 1;
        }
        const entry = nextQueries[ix];

        // The split state always mirrors the server's verdict for this turn (set,
        // changed, or cleared by the assistant's split directive).
        const splitByColumn = safeString(queryResult.SplitByColumn ?? queryResult.splitByColumn).trim();

        const updated = !!(queryResult.Updated ?? queryResult.updated);
        const nextQueryText = safeString(queryResult.Query ?? queryResult.query);
        if (updated && !accentUtils.isEmpty(nextQueryText)) {
          const version = pushVersion(`query:${entry.id}`, nextQueryText, queryLabel(entry));
          if (version) {
            // Also surface the version that was current before this change, so the
            // user can restore it with one click.
            const prior = preTurn[`query:${entry.id}`];
            if (prior && prior.id !== version.id) {
              versionsForMessage.push({ ...prior, testOk: null });
            }
            versionsForMessage.push({ ...version, testOk: gatedOk(!!(queryResult.TestOk ?? queryResult.testOk)) });
          }
          nextQueries[ix] = { ...entry, query: nextQueryText, splitByColumn, editorKey: entry.editorKey + 1, lastResult: queryResult };
        } else {
          nextQueries[ix] = { ...entry, splitByColumn, lastResult: queryResult };
        }
        if (updated) announceQueryResult(queryLabel(entry), queryResult);

        // Only worth announcing on turns that actually changed something: repeating it on
        // every conversational turn buries the messages that matter (review, corrections).
        if (updated && (queryResult.SampleRowsUsedForTests ?? queryResult.sampleRowsUsedForTests)) {
          pushMsg('system', `${queryLabel(entry)}: ${t('the query returned no rows, so outputs were tested against sample rows from the entity (real field names, not your query result).')}`);
        }
      }
      if (removedQueryNames.size > 0) {
        nextQueries = nextQueries.filter((q) => !removedQueryNames.has(q.name.toLowerCase()));
      }
      setQueries(nextQueries);

      // Output updates (including brand-new outputs the AI introduced).
      const shapeResults = res?.Shapes ?? res?.shapes ?? [];
      let nextOutputs = [...outputs];
      for (const shape of shapeResults) {
        const name = safeString(shape?.Name ?? shape?.name).trim();
        if (!name) continue;
        const updated = !!(shape.Updated ?? shape.updated);
        const tested = !!(shape.Tested ?? shape.tested);
        const script = safeString(shape.Script ?? shape.script);
        const testOk = !!(shape.TestOk ?? shape.testOk);
        const error = safeString(shape.Error ?? shape.error);
        const valueJson = safeString(shape.ValueJson ?? shape.valueJson);

        let ix = nextOutputs.findIndex((o) => o.name.toLowerCase() === name.toLowerCase());
        if (ix < 0) {
          if (!updated || !aiMayAddOutputs) continue;
          nextOutputs.push({ id: makeId(), name, script: '', editorKey: 0, testOk: null, error: '', valueJson: '' });
          ix = nextOutputs.length - 1;
        }

        const entry = nextOutputs[ix];
        if (updated && !accentUtils.isEmpty(script)) {
          const version = pushVersion(`output:${entry.id}`, script, entry.name || name, { valueJson });
          if (version) {
            const prior = preTurn[`output:${entry.id}`];
            if (prior && prior.id !== version.id) {
              versionsForMessage.push({ ...prior, testOk: null });
            }
            versionsForMessage.push({ ...version, testOk: gatedOk(testOk) });
          }
          nextOutputs[ix] = { ...entry, script, editorKey: entry.editorKey + 1, testOk: gatedOk(testOk), error, valueJson, edited: false };
        } else if (tested) {
          nextOutputs[ix] = { ...entry, testOk: entry.script ? gatedOk(testOk) : entry.testOk, error, valueJson };
        }
        // Not updated and not tested (a conversational turn): keep the entry's previous
        // state — default TestOk=false from an untested result must not look like a failure.
      }
      if (removedOutputNames.size > 0) {
        nextOutputs = nextOutputs.filter((o) => !removedOutputNames.has(safeString(o.name).trim().toLowerCase()));
      }
      setOutputs(nextOutputs);

      if (removedQueryNames.size > 0 || removedOutputNames.size > 0) {
        const removedNote = [
          ...[...removedQueryNames].map((n) => `${t('query')} '${n}'`),
          ...[...removedOutputNames].map((n) => `${t('output')} '${n}'`),
        ].join(', ');
        pushMsg('system', `${t('Removed from this session')}: ${removedNote}`);
      }

      // Review-checklist turn result: the server ships the FULL merged checklist only
      // when the turn actually changed it (ChecklistUpdated); otherwise the client
      // keeps what it holds — including the user's tick states, which never leave
      // the client except via the request round-trip above.
      if (res?.ChecklistUpdated ?? res?.checklistUpdated) {
        const mergedGroups = parseChecklistGroups(safeString(res?.ChecklistJson ?? res?.checklistJson));
        if (mergedGroups) {
          setChecklistGroups(mergedGroups);
          pushMsg('system', t('The review checklist was updated — work through the items in the Checklist tab.'));
        }
      }

      // Plan-first turn result: an updated plan replaces the held one. Only a REVISION
      // (new plan, or changed goal/steps — PlanRevised) resets the approval: every
      // revision needs the user's explicit go-ahead before the assistant may ship
      // artifacts (the server enforces the same gate). A status-only update (steps
      // marked done while executing) is progress on the approved plan and keeps it.
      const turnAction = safeString(res?.Action ?? res?.action).trim().toLowerCase();
      if (hasPlan && (res?.PlanUpdated ?? res?.planUpdated)) {
        const nextPlan = parsePlan(safeString(res?.PlanJson ?? res?.planJson));
        if (nextPlan) {
          setPlan(nextPlan);
          if (res?.PlanRevised ?? res?.planRevised) {
            setPlanApproved(false);
            // A revision that arrived WITH a question must not steer the user to Approve:
            // a live run put "click Approve" right under "Date 2 must be configured
            // first", and the user approved twice without noticing the question.
            pushMsg('system', turnAction === 'question'
              ? t('The assistant revised the plan but needs your answer to the question above before it can proceed.')
              : t('The assistant proposed a plan — review it in the Plan panel and click Approve to let it author the artifacts.'));
          }
        }
      }

      // Document-parameter turn result: the server ships the FULL normalized declaration
      // set only when the turn changed it (DocumentParamsUpdated); otherwise the client
      // keeps what it holds.
      if (hasDocumentParams && (res?.DocumentParamsUpdated ?? res?.documentParamsUpdated)) {
        const nextParams = safeString(res?.DocumentParamsJson ?? res?.documentParamsJson).trim();
        if (nextParams) {
          setDocumentParamsJson(nextParams);
          let count = 0;
          try { count = JSON.parse(nextParams).length; } catch { count = 0; }
          pushMsg('system', count > 0
            ? t('The print-time parameters were updated ({{count}} declared) — they are written to the Parameters tab when you accept.', { count })
            : t('The print-time parameters were cleared — none are declared now.'));
        }
      }

      // The server's oracle round record (the brief + planner answer) travels as a hidden
      // "tool" message: never rendered, but round-tripped with the transcript so the next
      // turn's assistant can refine the previous brief instead of reconstructing intent.
      const toolRecord = safeString(res?.ToolRecord ?? res?.toolRecord);
      if (toolRecord) {
        pushMsg('tool', toolRecord);
      }

      // Test template values the find-record skill discovered in real rows; rendered as
      // one-click "use this value" chips on the assistant message.
      const suggestedTemplateValues = res?.SuggestedTemplateValues ?? res?.suggestedTemplateValues ?? null;
      const templateSuggestions = suggestedTemplateValues && typeof suggestedTemplateValues === 'object'
        ? Object.entries(suggestedTemplateValues)
            .map(([token, value]) => ({ token: safeString(token), value: safeString(value) }))
            .filter((s) => s.token && s.value)
        : [];

      // A round of intent-intake questions rides the assistant message as a checkbox
      // card (see IntakeQuestionsCard). The parsed questions persist on the message,
      // so a resumed session re-renders the card — answered rounds stay frozen via
      // the intakeAnswered/intakeSelections markers the submit handler adds.
      const intakeQuestions = parseIntakeQuestions(res?.IntakeQuestionsJson ?? res?.intakeQuestionsJson);

      // A "save as reference" proposal (the proposeAsset companion): the pasted material
      // the assistant thinks later sessions will need, rendered as a card with Save /
      // Dismiss on the assistant message. The card freezes once decided (proposalDecision).
      const proposedAsset = hasDependentAssets ? normalizeProposedAsset(res?.ProposedAsset ?? res?.proposedAsset) : null;

      pushMsg('assistant', message || t('I could not produce a response. Please try rephrasing.'), {
        versions: versionsForMessage.length > 0
          ? versionsForMessage.map((v) => ({ id: v.id, seq: v.seq, label: v.label, testOk: v.testOk }))
          : undefined,
        templateSuggestions: templateSuggestions.length > 0 ? templateSuggestions : undefined,
        intakeQuestions: intakeQuestions ?? undefined,
        proposedAsset: proposedAsset ?? undefined,
        // The server's turn verdict ("question" | "update" | "none"): the plan panel
        // holds Approve while the latest assistant turn is an unanswered question.
        action: turnAction || undefined,
      });

      // Server-side notices, routed by declared severity. Real failures stay as
      // individual red bubbles; warnings/info (mostly disclosed API limitations) are
      // grouped into ONE collapsible "Limitations & notes" list — attached to the review
      // bubble below when a review ran — so a turn with several honest caveats doesn't
      // render as a wall of amber.
      const softNotices = [];
      for (const notice of notices) {
        if (notice.severity === 'error') {
          pushMsg('error', notice.text);
        } else {
          softNotices.push(notice);
        }
      }

      for (const shape of shapeResults) {
        const updated = !!(shape.Updated ?? shape.updated);
        if (!updated) continue;
        const name = safeString(shape.Name ?? shape.name);
        const testOk = !!(shape.TestOk ?? shape.testOk);
        const error = safeString(shape.Error ?? shape.error);
        if (testOk && !reviewFailed) {
          pushMsg('system', `Output '${name}' test passed.`);
        } else if (error) {
          pushMsg('error', `Output '${name}' test failed: ${friendlyError(error)}`);
        }
      }

      // Automated semantic review of the turn (runs when everything passed its tests).
      // Rendered as ONE graded 'review' message — score-colored bubble, not an error —
      // present even on clean passes so the user sees the check actually ran. Only
      // reviewFailed (unresolved defects) gates the green success indicators.
      if (reviewScore != null || reviewNotes.length > 0 || reviewAdvisories.length > 0) {
        pushMsg('review',
          reviewSummary
            || (reviewFailed
              ? t('The automated review found issues the assistant could not fix automatically.')
              : t('The automated review checked this turn\'s artifacts.')),
          {
            review: {
              score: reviewScore,
              confidence: reviewConfidence,
              failed: reviewFailed,
              defects: reviewNotes,
              advisories: reviewAdvisories,
              notices: softNotices,
            },
          });
      } else if (softNotices.length > 0) {
        // No review verdict this turn (planner failure, review call failed, ...) — the
        // grouped notes still need a home of their own.
        pushMsg('notices', t('Limitations & notes'), { notices: softNotices });
      }

      // Context validation: the session ran with missing/dropped context (see the server's
      // profile context contract). Neutral note — the artifacts still work, but answer
      // quality is degraded and someone should fix the host wiring.
      const contextWarnings = (res?.ContextWarnings ?? res?.contextWarnings ?? []).map(safeString).filter(Boolean);
      const contextWarningsKey = contextWarnings.join('|');
      if (contextWarnings.length > 0 && lastContextWarningsRef.current !== contextWarningsKey) {
        pushMsg('system', `${t('Assistant context is incomplete')}: ${contextWarnings.join(' \u2022 ')}`);
      }
      lastContextWarningsRef.current = contextWarningsKey;

      return true;
    } catch (err) {
      // Server errors may be localization keys (e.g. the AI usage limit); resolve them.
      const raw = safeString(err?.message) || safeString(err);
      pushMsg('error', raw ? t(raw) : t('AI request failed.'));
      return false;
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [aiMayAddOutputs, allowAddQueries, announceQueryResult, apiVersion, bindingFor, buildServerMessages, buildSession, serverSessionKey, changeDiffJson, checklistGroups, collectedIntent, documentParamsJson, explainContextJson, getVersionList, hasDocumentParams, hasHostLanes, hostLanes, laneArtifacts, hasDependentAssets, dependentAssets, dismissedAssetProposals, hasPlan, hostContextJson, hostLabel, initialVariablesJson, intentStatement, outputs, plan, planApproved, productID, profile.hasChecklist, profile.resultShape, pushMsg, pushVersion, queries, queryAllowedTemplates, queryLabel, runtimeNotes, sessionOptions, sessionProfile, shapeAllowedTemplates, shapeKind, templateValues]);

  // ── Server-kept session: resume ────────────────────────────────────────────────
  //
  // The document says how the last turn stands. A response the dialog never applied
  // (the browser closed while the server finished) is applied now, as a live turn's
  // would be. A turn that failed with no dialog to tell is told now, its prompt back in
  // the composer. A turn still running (the lease is live) keeps the composer waiting
  // while the dialog polls for the outcome.
  const onChatPromptRef = React.useRef(onChatPrompt);
  onChatPromptRef.current = onChatPrompt;
  const applyResumedDocument = React.useCallback((document) => {
    if (document?.hasUnappliedTurn && document.lastTurn?.response) {
      pushMsg('system', t('The reply to your last message arrived while the assistant was closed - applying it now.'));
      // The applied state is newer than the server's snapshot: the sync writes it as
      // soon as the state settles (the as-opened state is not the baseline here).
      snapshotSyncRef.current.armed = true;
      snapshotSyncRef.current.lastJson = null;
      onChatPromptRef.current({
        text: safeString(document.lastTurn.prompt),
        history: [],
        intent: safeString(document.lastTurn.intent) || undefined,
        images: [],
        resumeResponse: document.lastTurn.response,
      });
      return;
    }
    if (document?.pendingTurn?.error) {
      pushMsg('error', `${t('The last message did not finish')}: ${t(document.pendingTurn.error)} ${t('Send again.')}`);
      if (document.pendingTurn.prompt) chatPromptRef.current?.setDraft?.(document.pendingTurn.prompt);
    }
  }, [pushMsg]);
  React.useEffect(() => {
    if (!serverSessionKey || serverSession?.info?.turnInProgress) return;
    applyResumedDocument(serverSession?.document);
    // Resume belongs to dialog open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (!serverSessionKey || !serverTurnRunning) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      let loaded = null;
      try {
        loaded = await loadAssistantSession(serverSessionKey);
      } catch {
        loaded = null;
      }
      if (cancelled) return;
      if (loaded && !loaded.info?.turnInProgress) {
        setServerTurnRunning(false);
        onSessionInfoChange(loaded.info);
        applyResumedDocument(loaded.document);
        return;
      }
      timer = setTimeout(tick, SERVER_TURN_POLL_MS);
    };
    timer = setTimeout(tick, SERVER_TURN_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [serverSessionKey, serverTurnRunning, applyResumedDocument, onSessionInfoChange]);

  // One intake round answered: the ticked options (and any typed "Other" answer)
  // become the next chat turn (prose the assistant reads like any answer) and the card
  // freezes on its message (the markers ride the saved session, so resumed transcripts
  // keep the record).
  //
  // INTENT of the answers turn: an option may carry an intent ('ask' | 'change') when
  // the question disambiguates "answer me" from "change the artifacts"; a pick decides
  // the turn's intent. Otherwise the answers inherit the intent of the user message
  // that provoked the question (so a clarification asked on an Ask turn does not turn
  // the reply into a change request). Plan-first hosts always author on 'change'.
  //
  // COLLECTED INTENT only matters to review (checklist) sessions — the server reads the
  // intent statement in the change-under-review section alone — so the answers join it
  // there only; elsewhere the transcript carries them.
  const submitIntakeAnswers = React.useCallback((messageId, questions, selections, otherText) => {
    const list = Array.isArray(questions) ? questions : [];
    const answered = list.map((q) => formatIntakeAnswerLine(q, selections?.[q.id], otherText?.[q.id]));
    if (answered.length === 0) return;

    setChatMessages((prev) => (Array.isArray(prev) ? prev : []).map((msg) => (
      msg.id === messageId
        ? { ...msg, intakeAnswered: true, intakeSelections: selections, intakeOtherText: otherText }
        : msg
    )));

    const provokingIntent = (() => {
      const index = chatMessages.findIndex((m) => m?.id === messageId);
      for (let i = (index < 0 ? chatMessages.length : index) - 1; i >= 0; i--) {
        if (safeString(chatMessages[i]?.role || 'user') === 'user') return safeString(chatMessages[i]?.intent);
      }
      return '';
    })();
    const intent = hasPlan ? 'change' : (resolveIntakeIntent(list, selections) ?? provokingIntent);

    const collectsIntent = !!profile.hasChecklist;
    const nextCollectedIntent = collectsIntent
      ? [safeString(collectedIntent).trim(), ...answered].filter(Boolean).join('\n')
      : undefined;
    if (collectsIntent) setCollectedIntent(nextCollectedIntent);

    const text = `${t('My answers:')}\n${answered.map((line) => `- ${line}`).join('\n')}`;
    pushMsg('user', text, { intent });
    const history = [
      ...chatMessages.map((m) => ({
        role: safeString(m?.role || 'user'),
        text: safeString(m?.text),
        intent: safeString(m?.intent),
      })),
      { role: 'user', text, intent },
    ].filter((m) => m.text.trim());
    onChatPrompt({ text, history, intent, collectedIntentOverride: nextCollectedIntent });
  }, [chatMessages, collectedIntent, hasPlan, onChatPrompt, profile.hasChecklist, pushMsg, setChatMessages]);

  // "Save as reference" decided. Save: the text goes to the asset library through the
  // ordinary asset API (verbatim, with the proposed content type) and the key joins the
  // session's references; the assistant reads it from the next turn on. Dismiss: the
  // key is remembered so the assistant does not propose it again. Either way the card
  // freezes on its message (the decision rides the saved session).
  const decideProposedAsset = React.useCallback(async (messageId, proposal, accept) => {
    const freeze = (decision, savedKey) => setChatMessages((prev) => (Array.isArray(prev) ? prev : []).map((msg) => (
      msg.id === messageId ? { ...msg, proposalDecision: decision, ...(savedKey ? { proposalSavedKey: savedKey } : {}) } : msg
    )));
    if (!accept) {
      setDismissedAssetProposals((prev) => (prev.includes(proposal.key) ? prev : [...prev, proposal.key]));
      freeze('dismissed');
      return;
    }
    try {
      // A proposal naming a session file keeps the stored bytes through the chip's own
      // "keep" (the chip shows the bookmark, the key joins the references, the chat is
      // told); pasted text goes to the library as a text asset.
      if (proposal.sessionDocumentId) {
        const kept = await sessionFilesRef.current.keep({ id: proposal.sessionDocumentId, name: proposal.sessionDocumentName }, { key: proposal.key, description: proposal.description || null });
        if (kept?.Key) freeze('saved', kept.Key);
        return;
      }
      const saved = await saveCustomizationAssetText({
        key: proposal.key,
        description: proposal.description || null,
        text: proposal.text,
        contentType: proposal.contentType,
      });
      if (isFrameworkError(saved)) return; // the framework already showed the server's message (e.g. key taken)
      if (!saved || typeof saved !== 'object' || !saved.Key) {
        pushMsg('error', t('The reference could not be saved.'));
        return;
      }
      setDependentAssets((prev) => {
        const next = normalizeDependentAssets(prev).filter((a) => a.key !== saved.Key);
        next.push({
          key: saved.Key,
          purpose: proposal.purpose === 'runtime' && profile.dependentAssetsAllowRuntime === true ? 'runtime' : 'reference',
          ...(saved.ContentHash ? { designedFromHash: safeString(saved.ContentHash).toLowerCase() } : {}),
        });
        return next;
      });
      freeze('saved', saved.Key);
      onDependentAssetAdded(saved, proposal.purpose);
    } catch (err) {
      pushMsg('error', `${t('The reference could not be saved')}: ${safeString(err?.message) || safeString(err)}`);
    }
  }, [onDependentAssetAdded, profile.dependentAssetsAllowRuntime, pushMsg, setChatMessages]);

  // Plan approved: flip the approval (sent as an override because this SAME turn is
  // the execute turn) and send the go-ahead as a change turn — the assistant now
  // authors the artifacts the plan promised. The plan stays visible as the record of
  // what was agreed; a later revision resets the approval.
  // The plan is BLOCKED on the user while the latest assistant turn is an unanswered
  // question (a prose question, or an intake card not yet submitted): approving then
  // just re-sends a canned go-ahead the assistant cannot act on. The panel holds the
  // Approve button and says so until the question is answered.
  const planBlockedByQuestion = React.useMemo(() => {
    if (!hasPlan || !plan || planApproved) return false;
    for (let i = chatMessages.length - 1; i >= 0; i--) {
      const m = chatMessages[i];
      if (m?.role === 'user') return false;
      if (m?.role !== 'assistant') continue;
      if (m.intakeQuestions && !m.intakeAnswered) return true;
      return safeString(m.action).toLowerCase() === 'question';
    }
    return false;
  }, [chatMessages, hasPlan, plan, planApproved]);

  const approvePlan = React.useCallback(() => {
    if (!hasPlan || !plan || planApproved) return;
    setPlanApproved(true);
    const text = t('The plan is approved — carry it out and produce the artifacts.');
    pushMsg('user', text, { intent: 'change' });
    const history = [
      ...chatMessages.map((m) => ({
        role: safeString(m?.role || 'user'),
        text: safeString(m?.text),
        intent: safeString(m?.intent),
      })),
      { role: 'user', text, intent: 'change' },
    ].filter((m) => m.text.trim());
    onChatPrompt({ text, history, intent: 'change', planApprovedOverride: true });
  }, [chatMessages, hasPlan, onChatPrompt, plan, planApproved, pushMsg]);

  const onRestoreVersion = React.useCallback((versionId) => {
    const version = getVersion(versionId);
    if (!version) return;

    // Restoring does NOT re-run the automated review, so warn once (not per click) that
    // the last verdict has not been re-checked against the restored artifacts.
    if (lastReviewFailedRef.current && !reviewNoteShownRef.current) {
      reviewNoteShownRef.current = true;
      pushMsg('system', t('Note: the automated review reported issues on this session and has not re-checked the restored version — its findings may or may not still apply.'));
    }

    if (version.key.startsWith('query:')) {
      const queryId = version.key.slice('query:'.length);
      setQueries((prev) => {
        const ix = prev.findIndex((q) => q.id === queryId);
        if (ix < 0) {
          pushMsg('system', t('The query for this version no longer exists.'));
          return prev;
        }
        const list = [...prev];
        list[ix] = { ...list[ix], query: version.content, editorKey: list[ix].editorKey + 1, lastResult: null };
        // A restored query has not RUN: the preview still shows the previous execution.
        pushMsg('system', `Restored ${version.label || t('query')} v${version.seq}. ${t('The preview still shows the last run — use Refresh to re-run with the restored query.')}`);
        // Hidden transcript record (role 'tool' round-trips to the server, never
        // rendered): without it the model's last knowledge of this artifact is its own
        // "I updated it" claim, so it keeps describing the rolled-back version.
        pushMsg('tool', `SESSION RECORD: the user RESTORED query '${version.label || queryId}' to version ${version.seq}. Anything earlier in this conversation describing a newer version of this query is now stale — the query text in the current session state is the truth. The restored query has not been run yet.`);
        return list;
      });
      return;
    }

    const outputId = version.key.startsWith('output:') ? version.key.slice('output:'.length) : '';
    setOutputs((prev) => {
      const ix = prev.findIndex((o) => o.id === outputId);
      if (ix < 0) {
        pushMsg('system', t('The output for this version no longer exists.'));
        return prev;
      }
      const restoredValueJson = safeString(version.valueJson);
      const list = [...prev];
      // Versions carry the value the script produced when it ran, so the preview
      // re-renders the restored version immediately; without one the value clears
      // and Refresh re-executes on demand.
      list[ix] = { ...list[ix], script: version.content, editorKey: list[ix].editorKey + 1, testOk: null, error: '', valueJson: restoredValueJson, edited: false };
      pushMsg('system', restoredValueJson
        ? `Restored ${version.label || t('output')} v${version.seq}.`
        : `Restored ${version.label || t('output')} v${version.seq}. ${t('This version has no stored result — use Refresh to run it.')}`);
      // Hidden transcript record (role 'tool' round-trips to the server, never
      // rendered): without it the model's last knowledge of this artifact is its own
      // "I updated it" claim, so it keeps describing the rolled-back version.
      pushMsg('tool', `SESSION RECORD: the user RESTORED output '${version.label || outputId}' to version ${version.seq}. Anything earlier in this conversation describing a newer version of this output is now stale — the output's expression in the current session state is the truth.`);
      return list;
    });
  }, [getVersion, pushMsg]);

  // Fills a template value the find-record skill discovered. The token is added to the
  // visible template list and the inputs are remounted (epoch) so the value shows.
  const onApplyTemplateSuggestion = React.useCallback((token, value) => {
    setTemplateValues((prev) => ({ ...(prev ?? {}), [token]: safeString(value) }));
    setLastPromptTokens((prev) => Array.from(new Set([...(prev ?? []), token])));
    setTemplateEpoch((e) => e + 1);
    pushMsg('system', `${t('Test value set')}: ${token} = ${safeString(value)}`);
  }, [pushMsg]);

  const renderMessageExtra = React.useCallback((m) => {
    const versions = Array.isArray(m?.versions) ? m.versions : [];
    const suggestions = Array.isArray(m?.templateSuggestions) ? m.templateSuggestions : [];
    const intake = Array.isArray(m?.intakeQuestions) ? m.intakeQuestions : [];
    const proposal = m?.proposedAsset && typeof m.proposedAsset === 'object' ? m.proposedAsset : null;
    if (versions.length === 0 && suggestions.length === 0 && intake.length === 0 && !proposal) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {proposal && (
          <ProposedAssetCard
            proposal={proposal}
            decision={m.proposalDecision}
            savedKey={m.proposalSavedKey}
            disabled={busy}
            onDecide={(accept) => decideProposedAsset(m.id, proposal, accept)}
          />
        )}
        {intake.length > 0 && (
          <IntakeQuestionsCard
            questions={intake}
            answered={!!m.intakeAnswered}
            selections={m.intakeSelections}
            otherText={m.intakeOtherText}
            disabled={busy}
            templateCatalog={intakeTemplateCatalog}
            onSubmit={(selections, otherText) => submitIntakeAnswers(m.id, intake, selections, otherText)}
          />
        )}
        {(versions.length > 0 || suggestions.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {versions.map((v) => (
              <AiArtifactVersionChip
                key={v.id}
                label={`${v.label || t('Artifact')} v${v.seq}`}
                ok={v.testOk}
                title={t('Restore this version into the editor')}
                onClick={() => onRestoreVersion(v.id)}
              />
            ))}
            {suggestions.map((s) => (
              <AiArtifactVersionChip
                key={s.token}
                label={`${s.token} = ${s.value}`}
                title={t('Use this found value as the test value for the template token')}
                onClick={() => onApplyTemplateSuggestion(s.token, s.value)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }, [busy, decideProposedAsset, intakeTemplateCatalog, onApplyTemplateSuggestion, onRestoreVersion, submitIntakeAnswers]);

  const onTestQueryClick = async (queryEntry, e) => {
    try {
      setStatus(STATUS_TESTING);
      pushMsg('system', `${t('Testing')} ${queryLabel(queryEntry)}...`);
      const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
        query: safeString(queryEntry.query),
        expectedResultShape: queryEntry.resultShape,
        apiVersion,
        queryLanguage: profile.queryLanguage,
        templateValues,
      }));
      updateQuery(queryEntry.id, { lastResult: res });
      announceQueryResult(queryLabel(queryEntry), res);
    } catch (err) {
      const errMsg = safeString(err?.message) || safeString(err);
      updateQuery(queryEntry.id, { lastResult: { TestOk: false, Error: errMsg } });
      pushMsg('error', `${queryLabel(queryEntry)}: ${t('test error')} — ${errMsg}`);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  // Customization sessions evaluate outputs through the customization endpoints (web
  // app in-process, customization-gated); workflow sessions keep the engine-backed
  // node-test endpoints so tests stay faithful to the engine runtime.
  const runOutputScriptTest = React.useCallback(
    (args) => (profile.nodeTestScope === 'customization' ? testCustomizationOutputScript(args) : testWorkflowSetVariable(args)),
    [profile.nodeTestScope]
  );

  // Profiles whose artifacts are not JavaScript expressions for the shared harness
  // (pdfDocument: a pdfscript function body rendered by the document engine) opt out of
  // the client-side output tests entirely; their server host adapter tests every
  // candidate during the chat turn and their preview component renders on its own.
  const clientOutputTests = profile.clientOutputTests !== false;

  const onTestOutputClick = async (output, e) => {
    if (!clientOutputTests) {
      e?.promise?.();
      return;
    }
    try {
      setStatus(STATUS_TESTING);
      const res = throwIfFrameworkException(await runOutputScriptTest({
        name: output.name || 'result',
        valueMode: 'javascript',
        valueTemplate: '',
        script: safeString(output.script),
        variablesJson: safeString(initialVariablesJson) || '{}',
        templateValues: { ...(templateValues ?? {}) },
        localsJson: JSON.stringify(buildOutputTestLocals()),
      }));
      const success = !!(res?.success ?? res?.Success);
      const error = safeString(res?.error ?? res?.Error);
      updateOutput(output.id, {
        testOk: success,
        error,
        valueJson: safeString(res?.valueJson ?? res?.ValueJson),
        edited: false,
      });
      if (success) {
        pushMsg('system', `Output '${output.name}' test passed.`);
      } else if (error) {
        pushMsg('error', `Output '${output.name}' test failed: ${friendlyError(error)}`);
      }
    } catch (err) {
      const errMsg = safeString(err?.message) || safeString(err);
      updateOutput(output.id, { testOk: false, error: errMsg, valueJson: '', edited: false });
      pushMsg('error', `Output test error: ${errMsg}`);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  /**
   * Merges preview-supplied template values (e.g. the report date-range picker's
   * computed instants) into the session's template values and remounts the inputs so
   * the Artifacts tab shows them.
   */
  const onTemplateValuesPatch = React.useCallback((patch) => {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return;
    setTemplateValues((prev) => ({ ...(prev ?? {}), ...patch }));
    setTemplateEpoch((e) => e + 1);
  }, []);

  /**
   * Re-runs every query, then re-tests every output against the fresh rows — the
   * profile preview's "Refresh" action (e.g. re-render an ad-hoc report on live data).
   * Fresh results are collected locally because the output tests must bind THIS run's
   * rows, not whatever state React has committed so far.
   *
   * templateValuePatch (optional): template values to merge in and run with THIS
   * refresh — the caller's state update has not committed yet, so the run must not rely
   * on the closed-over templateValues (e.g. the report date-range picker's new range).
   */
  const onRefreshAll = React.useCallback(async (templateValuePatch) => {
    const patch = templateValuePatch && typeof templateValuePatch === 'object' && !Array.isArray(templateValuePatch)
      ? templateValuePatch
      : null;
    if (patch) onTemplateValuesPatch(patch);
    const effectiveTemplateValues = patch ? { ...(templateValues ?? {}), ...patch } : templateValues;
    try {
      setStatus(STATUS_TESTING);

      // The queries are independent, so they run in PARALLEL — a refresh costs the
      // slowest query, not the sum. Each task catches its own failure so one bad query
      // never sinks the batch; announcements happen after, in stable query order.
      const runnable = queries.filter((q) => !accentUtils.isEmpty(q.query));
      const settled = await Promise.all(runnable.map(async (q) => {
        try {
          const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
            query: safeString(q.query),
            expectedResultShape: q.resultShape,
            apiVersion,
            queryLanguage: profile.queryLanguage,
            templateValues: effectiveTemplateValues,
            // Profiles whose real runtime honors the query verbatim (data export: the
            // query's own row limit is the sheet's) bound their previews here instead.
            maxPreviewRows: profile.previewMaxRows,
          }));
          return [q, res];
        } catch (err) {
          return [q, { TestOk: false, Error: safeString(err?.message) || safeString(err) }];
        }
      }));

      const freshResults = new Map();
      for (const [q, res] of settled) {
        freshResults.set(q.id, res);
        announceQueryResult(queryLabel(q), res);
      }
      setQueries((prev) => prev.map((q) => (freshResults.has(q.id) ? { ...q, lastResult: freshResults.get(q.id) } : q)));

      // Unlike the editors' 100-row test cap, refresh binds the FULL result rows: the
      // preview (e.g. a top-N report) must render exactly what the queries returned.
      const locals = {};
      for (const q of queries) {
        const binding = bindingFor(q);
        if (!binding) continue;
        const latest = freshResults.get(q.id) ?? q.lastResult;
        const rows = extractResultRows(latest?.Result ?? latest?.result);
        locals[binding] = rows;
        if (binding.endsWith('.rows')) {
          locals[`${binding.slice(0, -'.rows'.length)}.count`] = rows.length;
        }
      }

      // Outputs are independent of each other (they all bind the same fresh locals),
      // so they re-test in parallel too; per-task catches keep failures isolated.
      if (!clientOutputTests) return;
      await Promise.all(outputs
        .filter((output) => !accentUtils.isEmpty(output.script))
        .map(async (output) => {
          try {
            const res = throwIfFrameworkException(await runOutputScriptTest({
              name: output.name || 'result',
              valueMode: 'javascript',
              valueTemplate: '',
              script: safeString(output.script),
              variablesJson: safeString(initialVariablesJson) || '{}',
              templateValues: { ...(effectiveTemplateValues ?? {}) },
              localsJson: JSON.stringify(locals),
            }));
            const success = !!(res?.success ?? res?.Success);
            const error = safeString(res?.error ?? res?.Error);
            updateOutput(output.id, { testOk: success, error, valueJson: safeString(res?.valueJson ?? res?.ValueJson), edited: false });
            if (!success && error) {
              pushMsg('error', `Output '${output.name}' refresh failed: ${friendlyError(error)}`);
            }
          } catch (err) {
            const errMsg = safeString(err?.message) || safeString(err);
            updateOutput(output.id, { testOk: false, error: errMsg, valueJson: '', edited: false });
            pushMsg('error', `Output '${output.name}' refresh failed: ${errMsg}`);
          }
        }));
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [announceQueryResult, apiVersion, bindingFor, clientOutputTests, initialVariablesJson, onTemplateValuesPatch, outputs, profile.previewMaxRows, profile.queryLanguage, pushMsg, queries, queryLabel, runOutputScriptTest, templateValues, updateOutput]);

  // One automatic artifact run when the dialog opens over EXISTING artifacts (editing
  // a saved customization): outputs seed with their scripts but no evaluated value, so
  // previews that read output values (the data grid's declared definition) would
  // otherwise render nothing until the first manual Refresh or chat turn.
  const initialRunRef = React.useRef(false);
  React.useEffect(() => {
    if (initialRunRef.current) return;
    if (!clientOutputTests) return;
    const needsRun = outputs.some((o) => !accentUtils.isEmpty(o.script) && o.testOk == null && accentUtils.isEmpty(o.valueJson));
    if (!needsRun) return;
    initialRunRef.current = true;
    onRefreshAll();
    // Mount-only: the seeded artifacts are what decide whether an initial run is due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checklist sessions open with the review already underway: the profile's
  // autoFirstPrompt is sent automatically as a 'change' turn so the checklist exists
  // before the user has to ask for anything. Fresh opens with a diff only — resumed
  // transcripts and diff-less opens start idle.
  const autoPromptRef = React.useRef(false);
  React.useEffect(() => {
    const autoPrompt = safeString(profile.autoFirstPrompt).trim();
    if (!autoPrompt || autoPromptRef.current) return;
    if (Array.isArray(initialSession?.messages) && initialSession.messages.length > 0) return;
    if (!safeString(changeDiffJson).trim()) return;
    autoPromptRef.current = true;
    // Sent through the chat control so the auto-start behaves exactly like the user
    // clicking Send: transcript entry, intent label, and the Send button's built-in
    // spinner/self-disable for the duration of the turn.
    chatPromptRef.current?.send(t(autoPrompt), 'change');
    // Mount-only by design: eligibility is decided once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A host-supplied prompt (the editor's action buttons open the assistant with it) goes
  // through the same path as a click on Send - into a fresh chat, or into the chat under
  // way when the host says so (initialPromptIntoResumed). A prompt the host withholds from
  // a resumed transcript is left in the composer rather than dropped, so the user sees
  // what was about to be asked and can send it.
  React.useEffect(() => {
    const first = safeString(initialPrompt).trim();
    if (!first || autoPromptRef.current) return;
    autoPromptRef.current = true;
    if (!initialPromptIntoResumed && Array.isArray(initialSession?.messages) && initialSession.messages.length > 0) {
      chatPromptRef.current?.setDraft?.(first);
      return;
    }
    chatPromptRef.current?.send(first, initialPromptIntent === 'ask' ? 'ask' : 'change');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promptSuggestions = React.useMemo(() => {
    const list = Array.isArray(suggestedPrompts) ? suggestedPrompts : (Array.isArray(profile.suggestedPrompts) ? profile.suggestedPrompts.map((p) => t(p)) : []);
    return list.map((p) => safeString(p).trim()).filter((p) => p.length > 0);
  }, [suggestedPrompts, profile.suggestedPrompts]);

  const onAddQuery = () => {
    setQueries((prev) => {
      const taken = new Set(prev.map((q) => q.name.toLowerCase()));
      let n = prev.length + 1;
      let name = `query${n}`;
      while (taken.has(name.toLowerCase())) name = `query${++n}`;
      return [...prev, {
        id: makeId(),
        name,
        query: '',
        resultBinding: '',
        resultShape: profile.resultShape,
        splitByColumn: '',
        editorKey: 0,
        lastResult: null,
      }];
    });
  };

  const onRemoveQuery = (id) => {
    setQueries((prev) => prev.filter((q) => q.id !== id));
  };

  const onAddOutput = () => {
    setOutputs((prev) => [...prev, { id: makeId(), name: '', script: '', editorKey: 0, testOk: null, error: '', valueJson: '' }]);
  };

  const onRemoveOutput = (id) => {
    setOutputs((prev) => prev.filter((o) => o.id !== id));
  };

  // The saved Customization this session is bound to (updates in place after the
  // first save-as prompt creates the record).
  const [savedCustomization, setSavedCustomization] = React.useState(() => (
    customization?.id ? { id: customization.id, name: safeString(customization.name) } : null
  ));
  const [saving, setSaving] = React.useState(false);

  /**
   * The persisted definition: everything needed to rehydrate this dialog (and later
   * to run the artifact standalone). Stored as the Customization draft's JSON.
   */
  const buildCustomizationDefinition = React.useCallback(() => JSON.stringify({
    profile: profile.key,
    apiVersion: safeString(apiVersion),
    queries: queries.map((q) => ({
      name: q.name,
      query: safeString(q.query),
      resultBinding: safeString(q.resultBinding),
      resultShape: q.resultShape ?? null,
      splitByColumn: safeString(q.splitByColumn).trim(),
    })),
    outputs: outputs.map((o) => ({ name: o.name, script: safeString(o.script) })),
    templateValues,
    sessionOptions,
    // the references the design was made from travel with the saved definition (CustomizationDependentAssets.Member)
    ...(hasDependentAssets && dependentAssets.length > 0 ? { dependentAssets: normalizeDependentAssets(dependentAssets) } : {}),
  }), [apiVersion, outputs, profile.key, queries, sessionOptions, templateValues, hasDependentAssets, dependentAssets]);

  /**
   * Persists the session as a Customization draft (creating the record via the
   * name prompt on first save). Returns the saved { id, name }, or null when the
   * user canceled the name prompt or the save failed (an error message is pushed
   * to the chat in that case).
   */
  const saveCustomizationCore = async () => {
    try {
      setSaving(true);
      const definition = buildCustomizationDefinition();

      if (savedCustomization?.id) {
        const ok = await saveCustomizationDraft(savedCustomization.id, definition);
        if (ok !== true) throw new Error('save failed');
        onCustomizationSaved?.(savedCustomization);
        return savedCustomization;
      }

      const named = await showDialog(<SaveCustomizationDlg />);
      if (named?.canceled) return null;

      const id = await createCustomization({
        name: named.name,
        customizationType: profile.key,
        definition,
        description: named.description || null,
      });
      if (!id) throw new Error('create failed');

      const created = { id, name: named.name };
      setSavedCustomization(created);
      // A server-kept session begun on the unsaved customization follows the record now
      // it exists, so reopening the customization finds this conversation.
      if (serverSessionKey && sessionGroup) {
        rehostAssistantSession(serverSessionKey, { ...sessionGroup, hostEntity: sessionGroup.hostEntity || 'Customization', hostEntityID: id }, named.name)
          .then((info) => { if (info) onSessionInfoChange(info); });
      }
      onCustomizationSaved?.(created);
      return created;
    } catch {
      pushMsg('error', t('Saving the customization failed.'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Workflow-host cross-save: saves the draft and keeps the session open.
  const doSaveCustomization = async (e) => {
    try {
      const saved = await saveCustomizationCore();
      if (saved) {
        pushMsg('assistant', `${t('Saved draft of')} **${saved.name}**. ${t('Publish it from the Customizations page to make it available.')}`);
      }
    } finally {
      e?.promise?.();
    }
  };

  // Customizations-page Save: saves the draft and closes the dialog. A failed save
  // (or a canceled name prompt) keeps the session open so nothing is lost.
  const doSaveAndClose = async (e) => {
    try {
      const saved = await saveCustomizationCore();
      if (saved) {
        closeWithSession({ canceled: false, customization: saved });
      }
    } finally {
      e?.promise?.();
    }
  };

  // "Send Chat as Bug" (shared flow — see reportAssistantBug.js): collect a
  // description, then email the transcript plus the server's diagnostic capture
  // (correlated by diagnosticSessionID) to the tenant's support address. The outcome
  // lands in the chat, so the user sees it in place.
  const [reportingBug, setReportingBug] = React.useState(false);
  const doReportBug = async (e) => {
    try {
      await promptAndReportAssistantBug({
        diagnosticSessionID: diagnosticSessionIdRef.current,
        hostLabel: safeString(hostLabel) || safeString(profile.dialogTitle) || profile.key,
        chatMessages,
        pushMsg,
        onSending: () => setReportingBug(true),
      });
    } finally {
      setReportingBug(false);
      e?.promise?.();
    }
  };

  // ── Server-kept session: state sync between turns ──────────────────────────────
  //
  // Every turn carries the dialog's state (sessionSnapshotJson) and the server saves it
  // with the turn; what changes BETWEEN turns (the response applied, checklist ticks,
  // template values typed, a plan approved) is synced here, debounced, so a resume
  // finds the state the user last saw rather than the state the last turn was sent
  // from. Quiet on failure: the next turn carries the state anyway.
  const flushSnapshotSync = React.useCallback((json) => {
    const sync = snapshotSyncRef.current;
    if (sync.timer) {
      clearTimeout(sync.timer);
      sync.timer = null;
    }
    if (!serverSessionKey || !json || json === sync.lastJson) return;
    sync.lastJson = json;
    saveAssistantSessionSnapshot(serverSessionKey, json);
  }, [serverSessionKey]);
  React.useEffect(() => {
    if (!serverSessionKey || busy) return undefined;
    const sync = snapshotSyncRef.current;
    const json = JSON.stringify(buildSession());
    if (!sync.armed) {
      // The state as opened IS the server's (or the host's own): nothing to write yet.
      sync.armed = true;
      sync.lastJson = json;
      return undefined;
    }
    if (json === sync.lastJson) return undefined;
    if (sync.timer) clearTimeout(sync.timer);
    sync.timer = setTimeout(() => flushSnapshotSync(json), SNAPSHOT_SYNC_DELAY_MS);
    return () => {
      if (sync.timer) {
        clearTimeout(sync.timer);
        sync.timer = null;
      }
    };
  }, [serverSessionKey, busy, buildSession, flushSnapshotSync]);
  // Unmount (close, or the picker switching sessions) writes what is pending.
  const latestSnapshotRef = React.useRef(null);
  latestSnapshotRef.current = serverSessionKey && !busy ? buildSession : null;
  React.useEffect(() => () => {
    const build = latestSnapshotRef.current;
    if (build && snapshotSyncRef.current.armed) flushSnapshotSync(JSON.stringify(build()));
  }, [flushSnapshotSync]);

  const closeWithSession = React.useCallback((args) => {
    // The FLAGGED checklist items (state 'unintended') ride every close path so the
    // host can offer to process them — each carries its causing diff path, which is
    // what lets the host navigate to the editor screen that produced the change.
    const flaggedItems = profile.hasChecklist
      ? checklistGroups.flatMap((g) => (Array.isArray(g.items) ? g.items : [])
        .filter((i) => i.state === 'unintended')
        .map((i) => ({
          id: i.id, text: i.text, path: i.path, category: i.category, severity: i.severity, groupTitle: g.title,
        })))
      : [];
    onClose?.({
      ...(args ?? {}),
      session: buildSession(),
      // The server-kept session's key, so a host that creates its record after the
      // assistant closed can move the session under it (rehostAssistantSession).
      ...(serverSessionKey ? { sessionKey: serverSessionKey } : {}),
      ...(flaggedItems.length > 0 ? { flaggedItems } : {}),
    });
  }, [onClose, buildSession, profile.hasChecklist, checklistGroups, serverSessionKey]);

  const doOk = async (e) => {
    // An artifact whose last test FAILED is handed over only on purpose: the chat
    // prose can claim success the test contradicts (a parameter read nobody declared,
    // a render error), and the badge is easy to miss while reading the reply.
    // Likewise a script edited by hand since its last test: the pass on display belonged
    // to the text that was replaced, so the edit is handed over only on purpose.
    const live = outputs.filter((o) => !!o.name.trim() && !accentUtils.isEmpty(o.script));
    const failed = live.filter((o) => o.testOk === false);
    const untested = live.filter((o) => o.testOk !== false && o.testOk !== true && !!o.edited);
    if (failed.length > 0 || untested.length > 0) {
      const failedNames = failed.map((o) => o.name.trim()).join(', ');
      const untestedNames = untested.map((o) => o.name.trim()).join(', ');
      const reasons = failed.map((o) => safeString(o.error).trim()).filter((r) => r.length > 0);
      const acceptAnyway = t('Accept anyway');
      const btn = await showOK(
        failed.length > 0 ? t('Test failed') : t('Not tested'),
        <div className="okDlgBody">
          {failed.length > 0 && <p>{t('The last test of {{names}} failed, so what you accept is not known to work.', { names: failedNames })}</p>}
          {reasons.map((r, ix) => <p key={ix} style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{r}</p>)}
          {untested.length > 0 && <p>{t('{{names}} was edited by hand after its last test, so what you accept is not known to work.', { names: untestedNames })}</p>}
          <p>{t('Ask the assistant to fix it, render the preview to test it, or accept it anyway and repair it in the editor.')}</p>
        </div>,
        ['application_strings.application.buttons.cancel', acceptAnyway],
        false, null, true, 600);
      if (btn !== acceptAnyway) {
        e?.promise?.();
        return;
      }
    }
    const namedQueries = queries
      .filter((q) => !!safeString(q.query).trim())
      .map((q) => ({ name: q.name, query: safeString(q.query) }));
    // Accepting the work means the design was checked against the library copies the
    // session read: every reference's designedFromHash moves to the current hash, so a
    // "changed" badge only ever means "changed since you last accepted".
    let acceptedDependentAssets = dependentAssets;
    if (hasDependentAssets && dependentAssets.length > 0) {
      try {
        acceptedDependentAssets = refreshDesignedFromHashes(dependentAssets, await loadAssetLibrary(true));
      } catch {
        acceptedDependentAssets = dependentAssets;
      }
    }
    closeWithSession({
      canceled: false,
      // Single-query hosts read `query`; multi-query hosts read `queries`.
      ...(hasQueries ? { query: safeString(queries[0]?.query), queries: namedQueries } : {}),
      outputs: outputs
        .filter((o) => !!o.name.trim())
        .map((o) => ({ name: o.name.trim(), script: safeString(o.script) })),
      // Document-parameter hosts read `documentParams` (the parsed declaration array).
      ...(hasDocumentParams ? { documentParams: parseDocumentParams(documentParamsJson) } : {}),
      // Host-lane hosts read `laneArtifacts` (lane key -> JSON string).
      ...(hasHostLanes ? { laneArtifacts } : {}),
      // Dependent-asset hosts read `dependentAssets` ([{ key, purpose, designedFromHash }]).
      ...(hasDependentAssets ? { dependentAssets: acceptedDependentAssets } : {}),
    });
    e?.promise?.();
  };

  // Preview tab: the profile plugin owns how its artifacts are visualised; the default
  // renders generic tables/values for profiles without a custom component.
  const PreviewComponent = profile.previewComponent ?? DefaultQueryShapingPreview;
  // The host context as an object, for previews that diff artifacts against the
  // host's data (e.g. the commission rule preview). Re-read per render of a function
  // host context so it tracks the editor's current state.
  const hostContextObject = React.useMemo(() => {
    const json = typeof hostContextJson === 'function' ? safeString(hostContextJson()) : safeString(hostContextJson);
    if (!json.trim()) return null;
    try {
      const parsed = JSON.parse(json);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
    // A function host context is re-snapshotted when the outputs change (the moment
    // a preview needs it); the string form is stable for the dialog's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostContextJson, outputs]);
  const previewQueries = queries.map((q) => ({
    id: q.id,
    name: q.name,
    query: q.query,
    splitByColumn: safeString(q.splitByColumn).trim(),
    lastResult: q.lastResult,
    rows: extractResultRows(q.lastResult?.Result ?? q.lastResult?.result),
  }));

  // Current version seq per artifact name ({ [name]: { queryVersion, mappingVersion } })
  // so a preview (e.g. data export) can stamp generated work with the exact artifact
  // versions it snapshotted. A seq is only reported while the live content still matches
  // that version; an unversioned or mid-edit artifact reports null.
  const getArtifactVersions = React.useCallback(() => {
    const bySheet = {};
    const entryFor = (name) => {
      const key = safeString(name).trim().toLowerCase();
      if (!key) return null;
      bySheet[key] = bySheet[key] ?? { queryVersion: null, mappingVersion: null };
      return bySheet[key];
    };
    for (const q of queries) {
      const entry = entryFor(q.name);
      if (!entry) continue;
      const latest = getVersionList(`query:${q.id}`).slice(-1)[0];
      entry.queryVersion = latest && latest.content === safeString(q.query) ? latest.seq : null;
    }
    for (const o of outputs) {
      const entry = entryFor(o.name);
      if (!entry) continue;
      const latest = getVersionList(`output:${o.id}`).slice(-1)[0];
      entry.mappingVersion = latest && latest.content === safeString(o.script) ? latest.seq : null;
    }
    return bySheet;
  }, [queries, outputs, getVersionList]);

  const tabButtonStyle = (selected) => ({
    border: 'none',
    borderBottom: selected ? '2px solid #6f42c1' : '2px solid transparent',
    background: 'none',
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: selected ? 700 : 500,
    color: selected ? '#6f42c1' : '#495057',
    cursor: 'pointer',
  });

  // User verdict on a checklist item (intended | unintended | back to unconfirmed).
  // Purely client state until the next chat turn round-trips it; the server treats
  // these states as the user's own and never lets the assistant change them.
  const onChecklistItemState = React.useCallback((itemId, nextState) => {
    setChecklistGroups((prev) => prev.map((g) => ({
      ...g,
      items: g.items.map((i) => (i.id === itemId ? { ...i, state: nextState } : i)),
    })));
  }, []);

  // Host preview slot: a single node becomes one unnamed tab; an array of
  // { title, content } becomes the left pane's tab set. When provided it REPLACES the
  // default Preview/Artifacts tabs (the host's screens are the session's real subject).
  // Checklist profiles lead with the Checklist tab (the session's deliverable); the
  // host's tabs (e.g. the Changes diff) follow it.
  const hostPreviewTabs = React.useMemo(() => {
    const hostTabs = previewContent
      ? (Array.isArray(previewContent) ? previewContent : [{ title: '', content: previewContent }])
        .filter((p) => p && p.content)
      : [];
    if (profile.hasChecklist) {
      return [
        {
          title: t('Checklist'),
          content: (
            <ChangeReviewChecklistPanel
              groups={checklistGroups}
              disabled={busy}
              onItemStateChange={onChecklistItemState}
            />
          ),
        },
        ...hostTabs,
      ];
    }
    return hostTabs.length > 0 ? hostTabs : null;
  }, [previewContent, profile.hasChecklist, checklistGroups, busy, onChecklistItemState]);
  const [hostTabIndex, setHostTabIndex] = React.useState(0);
  // Chat-only sessions (no queries, no outputs — the product review/explainer
  // profiles) have no artifacts to show: without host preview content the left pane
  // is hidden entirely and the conversation takes the full dialog.
  const chatOnly = profile.hasQueries === false && !hasOutputs;
  const showLeftPanel = !!hostPreviewTabs || !chatOnly;

  // Preview region snip (every profile): right-drag on the preview, or the camera
  // toggle + left-drag, rasterises the chosen rectangle and attaches it to the next
  // message exactly like a pasted screenshot — the user points at what is wrong
  // instead of describing where it is.
  const [snipMode, setSnipMode] = React.useState(false);
  const onSnipCapture = React.useCallback(({ dataUrl, page }) => {
    const added = chatPromptRef.current?.addImage?.({ dataUrl });
    if (!added) {
      pushMsg('error', `${t('Only')} ${MAX_PROMPT_IMAGES} ${t('images can be attached to one message. Remove one from the prompt first.')}`);
      return;
    }
    pushMsg('system', page
      ? `${t('Snip of preview page')} ${page} ${t('attached to the prompt. Describe what should change in that area.')}`
      : t('Snip of the preview attached to the prompt. Describe what should change in that area.'));
  }, [pushMsg]);
  const onSnipError = React.useCallback((err) => {
    pushMsg('error', `${t('Could not capture the preview')}: ${safeString(err?.message ?? err)}`);
  }, [pushMsg]);
  const snipButton = (
    <button
      type="button"
      data-tagname="snipPreview"
      title={snipMode ? t('Cancel snip') : t('Snip part of the preview into the prompt (or right-drag on the preview)')}
      aria-pressed={snipMode ? 'true' : 'false'}
      disabled={busy}
      onClick={() => setSnipMode((v) => !v)}
      style={{
        ...tabButtonStyle(false),
        marginLeft: 'auto',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: snipMode ? '#fff' : '#6f42c1',
        background: snipMode ? '#6f42c1' : 'none',
        borderRadius: snipMode ? 4 : 0,
        opacity: busy ? 0.5 : 1,
      }}
    >
      <LuCamera size={15} />
      <span style={{ fontSize: 12 }}>{snipMode ? t('Drag to snip') : t('Snip')}</span>
    </button>
  );

  return (
    <AccentDialog
      headerContent={hostLabel
        ? `${hostLabel} — ${t('AI Assistant')}`
        : (profile.dialogTitle
          ? t(profile.dialogTitle)
          : (hasQueries ? t('Query & Outputs AI Assistant') : t('Outputs AI Assistant')))}
      onClose={closeWithSession}
      relativeHeight={(d) => d.height - 120}
      relativeWidth={(d) => d.width - 120}
      // Actions live in the TITLE BAR (left of the X): the chat panel already fills
      // the dialog's foot with its own controls, so a bottom action bar reads as
      // clutter. Closing via the X is the cancel path.
      headerActionsContent={customizationEditor ? [
        <AccentButton
          key="reportBug"
          tagName="reportBug"
          outline
          disabled={reportingBug || chatMessages.length === 0}
          onClick={doReportBug}
          tooltip={t('Email this chat session and its diagnostic log to your support contact.')}
        >
          {reportingBug ? t('Sending...') : t('Send Chat as Bug...')}
        </AccentButton>,
        // Customizations-page editing: Save persists the draft and closes; the
        // dialog's own Cancel discards. No other actions — the artifacts have no
        // host editor to be handed to.
        <AccentButton
          key="save"
          tagName="save"
          disabled={busy || saving}
          onClick={doSaveAndClose}
          tooltip={t('Save the artifacts as this customization\'s draft and close. Publish from the Customizations page to make the changes available.')}
        >
          {saving ? t('Saving...') : t('Save')}
        </AccentButton>,
      ] : [
        <AccentButton
          key="reportBug"
          tagName="reportBug"
          outline
          disabled={reportingBug || chatMessages.length === 0}
          onClick={doReportBug}
          tooltip={t('Email this chat session and its diagnostic log to your support contact.')}
        >
          {reportingBug ? t('Sending...') : t('Send Chat as Bug...')}
        </AccentButton>,
        ...(allowSaveAsCustomization ? [
          <AccentButton
            key="saveCustomization"
            tagName="saveCustomization"
            outline
            disabled={busy || saving}
            onClick={doSaveCustomization}
            tooltip={savedCustomization
              ? t('Save the current artifacts as a new draft of this customization.')
              : t('Save the current artifacts as a reusable customization.')}
          >
            {saving ? t('Saving...') : (savedCustomization ? t('Save draft') : t('Save as customization...'))}
          </AccentButton>,
        ] : []),
        <AccentButton key="ok" tagName="ok" disabled={busy} onClick={doOk}>
          {profile.okLabel
            ? t(profile.okLabel)
            : (!hasOutputs ? t('Use query') : hasQueries ? t('Use query and outputs') : t('Use outputs'))}
        </AccentButton>,
      ]}
    >
      <div style={{ display: 'flex', gap: 16, height: '100%', minHeight: 0, background: '#fff' }} {...dialogDragHandlers}>

        {/* ── Left panel: host preview slot, or the default tabbed artifacts / preview ── */}
        {showLeftPanel && (
        <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {status === STATUS_GENERATING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Thinking...')}</div>
          )}
          {status === STATUS_TESTING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Testing...')}</div>
          )}

          {/* ── Plan panel (plan-first profiles): the gate before any artifact ── */}
          {hasPlan && (
            <PlanPanel
              plan={plan}
              approved={planApproved}
              disabled={busy}
              blocked={planBlockedByQuestion}
              onApprove={approvePlan}
            />
          )}

          {hostPreviewTabs && (
            <>
              <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #dee2e6' }}>
                {hostPreviewTabs.length > 1 && hostPreviewTabs.map((tab, index) => (
                  <button
                    key={index}
                    type="button"
                    data-tagname={`hostTab${index}`}
                    style={tabButtonStyle(hostTabIndex === index)}
                    onClick={() => setHostTabIndex(index)}
                  >
                    {safeString(tab.title) || t('Preview')}
                  </button>
                ))}
                {supportsSnip && snipButton}
              </div>
              <AccentRegionSnip disabled={busy || !supportsSnip} snipMode={snipMode} onSnipModeChange={setSnipMode} onCapture={onSnipCapture} onError={onSnipError}>
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {(hostPreviewTabs[hostTabIndex] ?? hostPreviewTabs[0]).content}
                </div>
              </AccentRegionSnip>
            </>
          )}

          {!hostPreviewTabs && (
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #dee2e6' }}>
            <button type="button" data-tagname="tabPreview" style={tabButtonStyle(activeTab === 'preview')} onClick={() => setActiveTab('preview')}>
              {t('Preview')}
            </button>
            <button type="button" data-tagname="tabArtifacts" style={tabButtonStyle(activeTab === 'artifacts')} onClick={() => setActiveTab('artifacts')}>
              {t('Artifacts')}
            </button>
            {activeTab === 'preview' && supportsSnip && snipButton}
          </div>
          )}

          {!hostPreviewTabs && activeTab === 'preview' && (
            <AccentRegionSnip disabled={busy || !supportsSnip} snipMode={snipMode} onSnipModeChange={setSnipMode} onCapture={onSnipCapture} onError={onSnipError}>
              <PreviewComponent
                queries={previewQueries}
                outputs={outputs}
                busy={busy}
                apiVersion={apiVersion}
                sessionOptions={sessionOptions}
                templateValues={templateValues}
                onTemplateValuesChange={onTemplateValuesPatch}
                onSessionOptionsChange={(patch) => setSessionOptions((prev) => ({ ...prev, ...patch }))}
                onRefresh={onRefreshAll}
                onDefect={(message) => pushMsg('error', safeString(message))}
                onOutputRendered={onOutputRendered}
                getArtifactVersions={getArtifactVersions}
                hostContext={hostContextObject}
                templateCatalog={templateCatalog}
                documentParamsJson={hasDocumentParams ? documentParamsJson : undefined}
                laneArtifacts={hasHostLanes ? laneArtifacts : undefined}
                // a preview that edits a host lane (the supplier integration's product worksheet) writes it here: the
                // change rides the next request and comes back to the host on close like the assistant's own updates
                onLaneArtifactsChange={hasHostLanes ? (patch) => setLaneArtifacts((prev) => ({ ...prev, ...(patch ?? {}) })) : undefined}
                // a preview's action buttons post into the chat under way ('ask' | 'change'), exactly like the composer's
                onSendPrompt={(text, intent) => chatPromptRef.current?.send(safeString(text), intent === 'ask' ? 'ask' : 'change')}
              />
            </AccentRegionSnip>
          )}

          {!hostPreviewTabs && activeTab === 'artifacts' && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>

          {usedTemplates.length > 0 && (
            <div style={sectionStyle}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Template values')}</div>
              {usedTemplates.map((token) => (
                <AccentTextUnbound
                  key={`${token}:${templateEpoch}`}
                  label={token}
                  disabled={busy}
                  defaultValue={safeString(templateValues?.[token])}
                  onChange={(value) => setTemplateValues((prev) => ({ ...(prev ?? {}), [token]: safeString(value) }))}
                  maxLength={4000}
                />
              ))}
            </div>
          )}

          {/* ── Queries ── */}
          {queries.map((queryEntry) => {
            const queryTestOk = !!(queryEntry.lastResult?.TestOk ?? queryEntry.lastResult?.testOk);
            const queryError = friendlyError(safeString(queryEntry.lastResult?.Error ?? queryEntry.lastResult?.error));
            const queryRowCount = queryEntry.lastResult?.ValueCount ?? queryEntry.lastResult?.valueCount;
            const queryRows = extractResultRows(queryEntry.lastResult?.Result ?? queryEntry.lastResult?.result);
            return (
              <div key={queryEntry.id} style={sectionStyle}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{queryLabel(queryEntry)}</div>
                {allowAddQueries && (
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1.5fr auto', alignItems: 'end' }}>
                    <AccentTextUnbound
                      label={t('Query name (rows available at ctx.queries.<name>)')}
                      disabled={busy}
                      defaultValue={queryEntry.name}
                      onChange={(v) => updateQuery(queryEntry.id, { name: safeString(v).trim() })}
                      maxLength={120}
                    />
                    <div style={{ paddingBottom: 2 }}>
                      <AccentButton outline size="sm" disabled={busy || queries.length <= 1} onClick={() => onRemoveQuery(queryEntry.id)}>
                        {t('Remove')}
                      </AccentButton>
                    </div>
                  </div>
                )}
                <AccentTemplateTextAreaUnbound
                  key={queryEntry.editorKey}
                  label={profile.queryLanguage === 'sql' ? t('SQL query') : t('OData query')}
                  rows={5}
                  max={8000}
                  templateCatalog={queryCatalog}
                  defaultValue={queryEntry.query}
                  onChange={(v) => updateQuery(queryEntry.id, { query: safeString(v) })}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <AccentButton tagName="testQuery" outline size="sm" disabled={busy || accentUtils.isEmpty(queryEntry.query)} onClick={(e) => onTestQueryClick(queryEntry, e)}>
                    {t('Test query')}
                  </AccentButton>
                  {!!queryEntry.lastResult && (
                    <TestOutcomeBadge ok={queryTestOk} error={queryTestOk ? '' : queryError} />
                  )}
                  {queryTestOk && queryRowCount != null && (
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{queryRowCount} {t('rows')}</span>
                  )}
                </div>
                {queryRows.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t('Result sample')}</summary>
                    <pre style={{ margin: '6px 0 0 0', padding: '6px 8px', borderRadius: 6, border: '1px solid #dee2e6', background: '#f8f9fa', maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                      {JSON.stringify(queryRows.slice(0, 5), null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}

          {allowAddQueries && (
            <div>
              <AccentButton outline size="sm" disabled={busy} onClick={onAddQuery}>{t('Add query')}</AccentButton>
            </div>
          )}

          {/* ── Outputs (hidden for query-only profiles) ── */}
          {hasOutputs && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{outputsLabel || t('Output Variables')}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              {outputsHint || (!allowAddOutputs
                ? t('A JavaScript expression over the query result. Ask the AI to write or refine it, or edit it here.')
                : hasQueries
                  ? t('Each output is a JavaScript expression that shapes the query result. Ask the AI to add or change outputs, or edit them here.')
                  : t('Each output is a JavaScript expression computed from the trigger context. Ask the AI to add or change outputs, or edit them here.'))}
            </div>

            {outputs.map((output) => (
              <div key={output.id} style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
                {allowAddOutputs && (
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1.5fr auto', alignItems: 'end' }}>
                  <AccentTextUnbound
                    label={t('Variable name')}
                    disabled={busy}
                    defaultValue={output.name}
                    onChange={(v) => updateOutput(output.id, { name: safeString(v).trim() })}
                    maxLength={120}
                  />
                  <div style={{ paddingBottom: 2 }}>
                    <AccentButton outline size="sm" disabled={busy} onClick={() => onRemoveOutput(output.id)}>
                      {t('Remove')}
                    </AccentButton>
                  </div>
                </div>
                )}
                {/* fixed outputs that need telling apart (several of them, or JSON documents): the contract's name
                    (and the profile's one-line purpose, when it has one) is the heading; a lone output is already
                    named by the section label */}
                {!allowAddOutputs && !!output.name && (profile.outputLanguage === 'json' || outputs.length > 1) && (
                  <div style={{ display: 'grid', gap: 2 }}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{output.name}</div>
                    {!!profile.outputDescriptions?.[output.name] && (
                      <div style={{ fontSize: 11, opacity: 0.7 }}>{t(profile.outputDescriptions[output.name])}</div>
                    )}
                  </div>
                )}
                <AccentTemplateTextAreaUnbound
                  key={output.editorKey}
                  label={profile.outputScriptLabel ? t(profile.outputScriptLabel) : (profile.outputLanguage === 'json' ? t('JSON') : t('JavaScript expression'))}
                  rows={4}
                  max={12000}
                  templateCatalog={jsCatalog}
                  insertMode="snippet"
                  defaultValue={output.script}
                  onChange={(v) => onOutputScriptEdited(output.id, safeString(v))}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {!profile.hideOutputTest && (
                    <AccentButton outline size="sm" disabled={busy || accentUtils.isEmpty(output.script)} onClick={(e) => onTestOutputClick(output, e)}>
                      {t('Test output')}
                    </AccentButton>
                  )}
                  <TestOutcomeBadge ok={output.testOk} error={output.testOk ? '' : output.error} edited={!!output.edited} />
                </div>
                {output.testOk && !!output.valueJson && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t('Evaluated value')}</summary>
                    <pre style={{ margin: '6px 0 0 0', padding: '6px 8px', borderRadius: 6, border: '1px solid #dee2e6', background: '#f8f9fa', maxHeight: 180, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                      {output.valueJson}
                    </pre>
                  </details>
                )}
              </div>
            ))}

            {allowAddOutputs && (
              <div>
                <AccentButton outline size="sm" disabled={busy} onClick={onAddOutput}>{t('Add output variable')}</AccentButton>
              </div>
            )}
          </div>
          )}
          </div>
          )}
        </div>
        )}

        {/* ── Right panel: conversation (full width for chat-only sessions without preview) ── */}
        <div style={showLeftPanel
          ? { flex: '0 0 40%', minWidth: 300, borderLeft: '1px solid #dee2e6', paddingLeft: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }
          : { flex: '1 1 0', minWidth: 300, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!showLeftPanel && status === STATUS_GENERATING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0', marginBottom: 8 }}><AccentSpinner size="sm" /> {t('Thinking...')}</div>
          )}
          {serverTurnRunning && (
            // A turn this dialog did not start is running on the server (the session was
            // reopened mid-turn): its outcome is applied when it lands.
            <div data-tagname="serverTurnRunning" style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0', marginBottom: 8 }}>
              <AccentSpinner size="sm" /> {t('A reply to your last message is still being written - it will appear here when it is ready.')}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6, minWidth: 0 }}>
            {serverSessionInfo && sessionGroup ? (
              <AssistantSessionHeader
                info={serverSessionInfo}
                host={sessionGroup}
                busy={busy}
                onSwitch={onSwitchSession}
                onNew={onNewSession}
                onInfoChange={onSessionInfoChange}
              />
            ) : (
              <div style={{ fontWeight: 700, fontSize: 13 }}>{t('Conversation')}</div>
            )}
            {lastTurnUsage && (
              <div
                style={{ fontSize: 10, opacity: 0.45, cursor: 'default', whiteSpace: 'nowrap' }}
                title={
                  `${t('Last request')}: ${lastTurnUsage.ai} ${t('AI calls')} \u00b7 ${lastTurnUsage.data} ${t('data API calls')}\n` +
                  `${t('This session')}: ${sessionUsageRef.current.ai} ${t('AI calls')} \u00b7 ${sessionUsageRef.current.data} ${t('data API calls')}\n` +
                  t('Counts every AI model call and data API query the assistant pipeline made (including automatic tests, repairs and probes).')
                }
              >
                {`${lastTurnUsage.ai} ${t('AI')} \u00b7 ${lastTurnUsage.data} ${t('data')}`}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AccentPromptHistory
              messages={chatMessages}
              fillHeight
              renderMessageExtra={renderMessageExtra}
            />
          </div>
          <div style={{ borderTop: '1px solid #dee2e6', paddingTop: 10, marginTop: 8 }}>
            {promptSuggestions.length > 0 && chatMessages.length === 0 && (
              // One-click starts for an empty session; each sends like a click on Send.
              <div data-tagname="suggestedPrompts" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: '#6c757d', alignSelf: 'center' }}>{t('Try')}:</span>
                {promptSuggestions.map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    data-tagname={`suggestedPrompt${i}`}
                    disabled={busy}
                    title={p}
                    onClick={() => chatPromptRef.current?.send(p, 'change')}
                    style={{
                      padding: '3px 10px', borderRadius: 12, border: '1px solid #90caf9', background: busy ? '#f1f5f9' : '#e3f2fd',
                      color: '#0d47a1', fontSize: 11, fontWeight: 600, cursor: busy ? 'default' : 'pointer',
                      maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            {hasDependentAssets && (
              // The customisation's design references: the library entries the assistant
              // reads every turn. Attach existing ones or upload documentation straight
              // into the library; a "changed" badge means the library copy moved on since
              // the design last relied on it (the assistant is told the same).
              <DependentAssetsCtrl
                compact
                assets={dependentAssets}
                onChange={setDependentAssets}
                allowRuntimePurpose={profile.dependentAssetsAllowRuntime === true}
                disabled={busy}
                onAdded={onDependentAssetAdded}
              />
            )}
            {hasDependentAssets && pasteHint && !hasSessionFiles && (
              // A long or structured paste is documentation the next session would need
              // again: the hint points at the library before the paste is sent. (With a
              // server-kept session the composer offers to keep the paste as a session
              // file instead, and the chip's bookmark takes it to the library.)
              <div data-tagname="pasteHint" style={{ fontSize: 11, color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px', marginBottom: 4 }}>
                {t('This looks like documentation or structured data. If later sessions will need it too, add it to the asset library ("Add to library" above) instead of pasting it - the assistant then reads it every turn without it being pasted again.')}
              </div>
            )}
            {hasLookupReferences && (
              // Reference data (suppliers, reps, statuses, products) is matched by NAME;
              // a pasted raw id is not a name. The right-click menu inserts a resolved
              // "Name (<lookup> <id>)" reference the assistant uses as-is.
              <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4 }}>
                {t('Tip: right-click in the prompt to insert products, suppliers, reps or statuses as resolved references rather than typing ids.')}
              </div>
            )}
            <AccentPromptChat
              ref={chatPromptRef}
              disabled={busy}
              messages={chatMessages}
              onMessagesChange={setChatMessages}
              onSendPrompt={onChatPrompt}
              askLabel={t('Ask a Question')}
              sendLabel={profile.changeActionLabel ? t(profile.changeActionLabel) : undefined}
              placeholder={profile.chatPlaceholder
                ? t(profile.chatPlaceholder)
                : (!hasOutputs
                  ? t('Ask for a query or changes to it...')
                  : hasQueries
                    ? t('Ask for a query, outputs, or changes to either...')
                    : t('Ask for output variables or changes to them...'))}
              templateCatalog={promptTemplateCatalog}
              promptMax={profile.promptMaxChars}
              {...(hasDependentAssets ? { onDraftChange: onComposerDraftChange } : {})}
              {...(hasSessionFiles ? {
                attachments: sessionFiles.attachments,
                onAttachFiles: sessionFiles.attachFiles,
                onDropText: sessionFiles.dropText,
                dropActive: dialogDragOver,
                onRemoveAttachment: sessionFiles.remove,
                onConfirmAttachment: sessionFiles.confirm,
                onOpenAttachment: sessionFiles.open,
                ...(hasDependentAssets ? { onKeepAttachment: sessionFiles.keep } : {}),
              } : {})}
            />
          </div>
        </div>
      </div>
    </AccentDialog>
  );
};

/**
 * The dialog's title, shared by the body and the wrapper's loading / error frames so a
 * server-kept session opens under the same header it runs under.
 */
const dialogTitle = ({ hostLabel, profile, hasQueries }) => (hostLabel
  ? `${hostLabel} — ${t('AI Assistant')}`
  : (profile.dialogTitle
    ? t(profile.dialogTitle)
    : (hasQueries ? t('Query & Outputs AI Assistant') : t('Outputs AI Assistant'))));

/**
 * The server-kept session around the body: opens the host's group (its newest active
 * session, or a new one) before the body mounts, so the body seeds from the server's
 * document exactly as it seeds from a host-held initialSession; the picker's switch /
 * new remount the body on the other session. The body's own state syncs back between
 * turns (see the body's snapshot sync), and a body that unmounts flushes what is pending
 * before the next one loads.
 */
const ServerSessionDlg = (props) => {
  const { sessionHost, sessionProfile = QUERY_SHAPING_DEFAULT_PROFILE, hostLabel, onClose, initialSession, existingQueries, apiVersion, initialSessionKey } = props;
  const profile = resolveQueryShapingProfile(safeString(sessionProfile));
  const group = React.useMemo(() => normalizeSessionHost(sessionHost, safeString(sessionProfile)), [sessionHost, sessionProfile]);
  const defaultTitle = safeString(sessionHost?.title).trim() || safeString(hostLabel).trim() || (profile.dialogTitle ? t(profile.dialogTitle) : t('AI Assistant'));
  const [state, setState] = React.useState({ loading: true, error: '', session: null, epoch: 0 });

  const open = React.useCallback(async (loader) => {
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const session = await loader();
      if (!session?.info?.sessionKey) throw new Error(t('The session could not be opened.'));
      setState((prev) => ({ loading: false, error: '', session, epoch: prev.epoch + 1 }));
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false, error: safeString(err?.message) || t('The session could not be opened.') }));
    }
  }, []);

  React.useEffect(() => {
    // A host that opened the session ahead of the dialog (the brief wizard takes files into it) names it; the
    // group's newest session, or a new one, otherwise - and if the named one is gone meanwhile.
    const named = safeString(initialSessionKey).trim();
    open(async () => (named ? await loadAssistantSession(named) : null) ?? openOrCreateAssistantSession(group, defaultTitle, apiVersion));
    // Opening belongs to dialog open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSwitchSession = React.useCallback((session) => {
    const key = safeString(session?.sessionKey ?? session);
    if (!key) return;
    open(() => loadAssistantSession(key));
  }, [open]);

  const onNewSession = React.useCallback((args) => {
    // After the open session was deleted the group's newest continues (or a new one);
    // otherwise a new session is wanted.
    if (args?.afterDelete) {
      open(() => openOrCreateAssistantSession(group, defaultTitle, apiVersion));
      return;
    }
    open(async () => {
      const info = await createAssistantSession(group, defaultTitle, apiVersion);
      return info?.sessionKey ? loadAssistantSession(info.sessionKey) : null;
    });
  }, [open, group, defaultTitle, apiVersion]);

  const onServerSessionInfoChange = React.useCallback((info) => {
    setState((prev) => (prev.session ? { ...prev, session: { ...prev.session, info } } : prev));
  }, []);

  if (!state.session) {
    // The frame the body will fill, while the session opens (or if it cannot).
    return (
      <AccentDialog
        headerContent={dialogTitle({ hostLabel, profile, hasQueries: Array.isArray(existingQueries) && existingQueries.length > 0 })}
        onClose={() => onClose?.({})}
        relativeHeight={(d) => d.height - 120}
        relativeWidth={(d) => d.width - 120}
      >
        <div data-tagname="sessionOpening" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 160, gap: 10, color: state.error ? '#c62828' : '#6c757d', fontSize: 13 }}>
          {state.error ? state.error : (<><AccentSpinner size="sm" /> {t('Opening your session...')}</>)}
        </div>
      </AccentDialog>
    );
  }

  const { session } = state;
  // A session the body has already applied turns to seeds from the server's snapshot; a
  // brand-new session (no snapshot yet) falls back to whatever the host held in memory.
  const seed = session.document?.snapshot && typeof session.document.snapshot === 'object'
    ? session.document.snapshot
    : initialSession;
  return (
    <>
      <QueryShapingAssistantDlgBody
        key={`${session.info.sessionKey}:${state.epoch}`}
        {...props}
        initialPrompt={state.epoch === 1 ? props.initialPrompt : undefined}
        initialSession={seed}
        serverSession={session}
        sessionGroup={group}
        onSwitchSession={onSwitchSession}
        onNewSession={onNewSession}
        onServerSessionInfoChange={onServerSessionInfoChange}
      />
      {state.loading && (
        // Switching sessions: the current body stays visible (and flushes on unmount)
        // while the next loads.
        <div style={{ position: 'fixed', inset: 0, zIndex: 10100, background: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, color: '#495057' }}>
          <AccentSpinner size="sm" /> {t('Opening session...')}
        </div>
      )}
    </>
  );
};

/**
 * The AI assistant dialog. With a sessionHost ({ hostEntity, hostEntityID, hostSubKey,
 * title }) the session is kept on the server — resumable from any browser, listed in the
 * session picker, purged to the most recent few per host — and the dialog opens the
 * host's newest session (or a new one). Without one the session lives in memory as
 * before (initialSession round-trips through onClose).
 */
export const AccentQueryShapingAssistantDlg = (props) => (props.sessionHost
  ? <ServerSessionDlg {...props} />
  : <QueryShapingAssistantDlgBody {...props} />);
